import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { fmtTs } from '../lib/globalTz';

type NotebookRiskLevel =
  | 'safe_preview_only'
  | 'read_only_kql_notebook'
  | 'requires_external_enrichment'
  | 'requires_secrets_or_api_keys'
  | 'contains_shell_or_file_operations'
  | 'blocked_or_unsafe'
  | 'preview_only'
  | 'read_only_kql_candidate'
  | 'requires_admin_review'
  | 'blocked_or_admin_review'
  | string;
type TrustedStatus = 'unreviewed' | 'trusted' | 'restricted' | 'blocked' | string;

type NotebookRiskAnalysis = {
  version: string;
  riskLevel: NotebookRiskLevel;
  riskLabel: string;
  riskScore: number;
  riskFlags: string[];
  reasons: string[];
  markdownSummary: string;
  needsInteractiveAuthentication: boolean;
  containsWriteOperations: boolean;
  containsShellCommands: boolean;
  containsFileOperations: boolean;
  containsPackageInstall: boolean;
  containsProcessExecution: boolean;
  usesExternalApis: boolean;
  requiresSecretsOrApiKeys: boolean;
  hasEmbeddedTokenOrSecretCandidate: boolean;
  hasSecretPrintingRisk: boolean;
  isReadOnlyKqlCandidate: boolean;
  hasNonReadOnlyKql: boolean;
  customerWorkspaceTokensEmbedded: boolean;
  detectedShellCommands: string[];
  detectedWriteOperations: string[];
  detectedFileOperations: string[];
  detectedSecretIndicators: string[];
  detectedAuthIndicators: string[];
  detectedExternalApiCalls: string[];
  detectedExternalDomains: string[];
  detectedNonReadOnlyKql: string[];
  recommendedTrustStatus: 'unreviewed' | 'trusted' | 'restricted' | 'blocked';
};

type NotebookGuardrailPolicy = {
  executionAvailable: boolean;
  phase: string;
  guardrails: Array<{
    id: string;
    label: string;
    defaultAction: 'block' | 'allow';
    capability: string | null;
    allowedByPermission: boolean;
    nonOverridable?: boolean;
    description: string;
  }>;
};

type NotebookExecutionEligibility = {
  executionAvailable: boolean;
  eligibleForFutureRunner: boolean;
  blockedByDefault: boolean;
  blockedReasons: string[];
  allowedOverrides: string[];
  warnings: string[];
};

type NotebookSource = {
  id: string;
  name: string;
  sourceType: string;
  repoOwner: string | null;
  repoName: string | null;
  repoUrl: string;
  branch: string;
  pathFilter: string | null;
  enabled: boolean;
  trustedDefault: boolean;
  lastSyncedAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  localRepoPath?: string | null;
  localRepoPreferred?: boolean;
};

type NotebookRuntimeCustomer = {
  id: string;
  name: string;
  tenantId?: string | null;
  subscriptionId?: string | null;
  resourceGroup?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  enabled?: boolean;
  lighthouseMode?: boolean;
  timezone?: string | null;
};

type NotebookLaunchSource = { id: string; label: string };

type NotebookCatalogItem = {
  id: string;
  sourceId: string;
  sourceName: string | null;
  sourceType: string | null;
  repoUrl: string | null;
  branch: string | null;
  notebookPath: string;
  notebookName: string;
  title: string;
  description: string | null;
  category: string | null;
  tags: string[];
  requiredTables: string[];
  detectedKql: string[];
  detectedUrls: string[];
  requiredPackages: string[];
  riskLevel: NotebookRiskLevel;
  riskLabel?: string;
  riskFlags: string[];
  riskAnalysis?: NotebookRiskAnalysis;
  trustedStatus: TrustedStatus;
  trustNotes: string | null;
  sourceUrl: string | null;
  rawUrl: string | null;
  contentSha: string | null;
  previewMarkdown: string | null;
  cellSummary: Array<{ index: number; type: string; firstLine: string; riskFlags: string[] }>;
  lastSyncedAt: string | null;
  lastValidatedAt: string | null;
  trustedByEmail: string | null;
  trustedAt: string | null;
  updatedAt: string | null;
  effectiveGuardrails?: NotebookGuardrailPolicy;
  executionEligibility?: NotebookExecutionEligibility;
};

type CatalogResponse = {
  ok: boolean;
  catalog: NotebookCatalogItem[];
  categories?: string[];
  summary?: {
    total: number;
    trusted: number;
    blocked: number;
    requiresReview: number;
    readOnlyKql: number;
    safePreview?: number;
    requiresSecrets?: number;
    shellOrFile?: number;
  };
};

type CustomNotebookTemplate = { slug: string; title: string; category: string; priority: number; maturity: string; notebookPath: string; status: 'done' | 'pending' | string; catalogId?: string | null; trustedStatus?: string | null; riskLevel?: string | null; lastSyncedAt?: string | null; updatedAt?: string | null; };
type CustomTemplatesResponse = { ok: boolean; sourceId: string; templates: CustomNotebookTemplate[]; summary: { total: number; done: number; pending: number } };
type CustomNotebookSchedule = { id: string; notebookId: string; notebookTitle: string; scheduleName: string; cadence: string; enabled: boolean; customerName?: string | null; workspaceName?: string | null; sourceModule?: string | null; lastStatus?: string | null; createdAt?: string | null; updatedAt?: string | null; };
type SourcesResponse = { ok: boolean; sources: NotebookSource[]; githubAuthenticated: boolean; preferLocalRepo?: boolean; allowGithubFallback?: boolean; autoPullLocalRepo?: boolean; localRepoRoot?: string };
type SyncRun = { id: string; sourceId: string; sourceName: string; status: string; importedCount: number; updatedCount: number; skippedCount: number; errorMessage?: string | null; details?: unknown; createdByEmail?: string | null; createdAt: string; completedAt?: string | null };

type NotebookRunArtifact = { id: string; artifactType: string; filename: string; mimeType?: string | null; contentText?: string | null; contentJson?: unknown; sizeBytes?: number | null; createdAt?: string | null };
type NotebookRun = {
  id: string;
  notebookId: string;
  notebookTitle: string;
  notebookPath: string;
  sourceName?: string | null;
  sourceUrl?: string | null;
  executionMode: string;
  status: string;
  customerId?: string | null;
  customerName?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  tenantId?: string | null;
  subscriptionId?: string | null;
  resourceGroup?: string | null;
  sourceModule?: string | null;
  sourceRecordId?: string | null;
  incidentId?: string | null;
  incidentTitle?: string | null;
  entityType?: string | null;
  entityValue?: string | null;
  cveId?: string | null;
  iocValue?: string | null;
  campaignId?: string | null;
  runtimeContext?: Record<string, unknown>;
  timeRange?: string | null;
  parameters?: Record<string, unknown>;
  riskLevel?: string | null;
  trustedStatus?: string | null;
  workerJobId?: string | null;
  workerStatus?: string | null;
  resultSummary?: Record<string, unknown>;
  artifactSummary?: Array<Record<string, unknown>>;
  errorMessage?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
  artifacts?: NotebookRunArtifact[];
};

type InteractiveSnapshot = { id: string; sessionId: string; snapshotPath: string; checksumSha256: string; sizeBytes?: number | null; savedByEmail?: string | null; savedAt: string; metadata?: Record<string, unknown> };
type InteractiveSession = {
  id: string;
  sessionId: string;
  notebookId: string;
  notebookTitle: string;
  notebookPath: string;
  notebookName: string;
  notebookFileName: string;
  sourceName?: string | null;
  sourceUrl?: string | null;
  workspacePath: string;
  notebookFilePath: string;
  jupyterBaseUrl: string;
  jupyterPort?: number | null;
  jupyterPid?: number | null;
  status: string;
  customerId?: string | null;
  customerName?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  tenantId?: string | null;
  subscriptionId?: string | null;
  resourceGroup?: string | null;
  sourceModule?: string | null;
  sourceRecordId?: string | null;
  runtimeContext?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
  riskLevel?: string | null;
  trustedStatus?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  startedAt?: string | null;
  lastOpenedAt?: string | null;
  lastActivityAt?: string | null;
  expiresAt: string;
  stoppedAt?: string | null;
  stoppedByEmail?: string | null;
  stopReason?: string | null;
  updatedAt?: string | null;
  snapshots?: InteractiveSnapshot[];
};
type InteractiveConfig = { enabled: boolean; workspaceRoot: string; basePort: number; portCount: number; idleMinutes: number; maxMinutes: number; refusesRoot: boolean; runAsUser: string; jupyterCommand: string };
type WorkerHealth = { ok?: boolean; enabled?: boolean; workerUrl?: string; status?: string; message?: string; health?: Record<string, unknown>; error?: string; };
type WorkerDependencyProfile = { id: string; label: string; description?: string; packages: string[]; recommendedFor?: string[] };
type WorkerInstallLog = { installId: string; status?: string; startedAt?: string; completedAt?: string; profiles?: string[]; packages?: string[]; installed?: string[]; skipped?: unknown[]; returnCode?: number };
type WorkerReadiness = {
  ok?: boolean;
  readiness?: {
    overallStatus?: string;
    matrix?: Array<{ id: string; label: string; status: string; details?: string }>;
    coreModules?: Array<Record<string, unknown>>;
    pipCheck?: Record<string, unknown>;
    startupWarnings?: string[];
  };
};
type WorkerDependencyStatus = {
  ok?: boolean;
  status?: {
    installEnabled?: boolean;
    installRunsAsRoot?: boolean;
    installUser?: string;
    pythonExecutable?: string;
    pythonPrefix?: string;
    pipExecutable?: string;
    core?: Array<Record<string, unknown>>;
    importStatus?: Array<Record<string, unknown>>;
    missingImports?: string[];
    packageHints?: string[];
    recommendedPackages?: string[];
    recommendedProfiles?: string[];
    profiles?: WorkerDependencyProfile[];
    allowlist?: string[];
    pipCheck?: Record<string, unknown>;
    lastInstallLogs?: WorkerInstallLog[];
    notes?: string[];
  };
  readiness?: WorkerReadiness['readiness'];
};
type NotebookCompatibility = { compatibilityLevel?: string; compatibilityLabel?: string; headlessReady?: boolean; supportedModes?: string[]; missingImports?: string[]; recommendedProfiles?: string[]; recommendedPackages?: string[]; reasons?: string[]; warnings?: string[]; nextBestAction?: string };
type DependencyCheckResponse = { ok: boolean; importRoots?: string[]; packages?: string[]; supportFileSummary?: Record<string, unknown>; dependencies?: WorkerDependencyStatus; readiness?: WorkerReadiness['readiness']; compatibility?: NotebookCompatibility; message?: string; };

type ViewMode = 'catalog' | 'custom' | 'interactive' | 'runs' | 'sources' | 'governance';

function statusClass(status: string): string {
  const value = status.toLowerCase();
  if (value.includes('trusted') || value === 'completed' || value === 'enabled' || value === 'active' || value.includes('read_only') || value.includes('safe')) return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200';
  if (value.includes('blocked') || value.includes('failed') || value.includes('unsafe')) return 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200';
  if (value.includes('review') || value.includes('external') || value.includes('partial') || value === 'restricted' || value === 'starting' || value.includes('secret') || value.includes('shell')) return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-200';
}

function pill(label: string, key?: string) {
  return <span key={key ?? label} className="inline-flex items-center rounded-full border border-slate-600/70 bg-slate-900/70 px-2 py-0.5 text-[11px] text-slate-200">{label}</span>;
}

function prettyRisk(risk: string): string {
  const labels: Record<string, string> = {
    safe_preview_only: 'Safe Preview Only',
    preview_only: 'Safe Preview Only',
    read_only_kql_notebook: 'Read-only KQL Notebook',
    read_only_kql_candidate: 'Read-only KQL Notebook',
    requires_external_enrichment: 'Requires External Enrichment',
    requires_secrets_or_api_keys: 'Requires Secrets / API Keys',
    requires_admin_review: 'Requires Admin Review',
    contains_shell_or_file_operations: 'Contains Shell / File Operations',
    blocked_or_unsafe: 'Blocked / Unsafe',
    blocked_or_admin_review: 'Blocked / Unsafe',
  };
  return labels[risk] ?? risk.replace(/_/g, ' ');
}

function shortDate(value?: string | null): string {
  if (!value) return '-';
  try { return fmtTs(value); } catch { return value; }
}

function formatBytes(value?: number | null): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function riskOf(item: NotebookCatalogItem): NotebookRiskAnalysis {
  const flags = item.riskFlags ?? [];
  return item.riskAnalysis ?? {
    version: 'phase2-client-fallback',
    riskLevel: item.riskLevel,
    riskLabel: item.riskLabel ?? prettyRisk(item.riskLevel),
    riskScore: Math.min(100, flags.length * 10),
    riskFlags: flags,
    reasons: flags.length ? flags.map((f) => `Detected ${f.replace(/_/g, ' ')}.`) : ['Re-validate this notebook to populate Phase 2 detailed risk analysis.'],
    markdownSummary: item.description ?? '',
    needsInteractiveAuthentication: flags.includes('interactive_auth') || flags.includes('interactive_or_secret_auth'),
    containsWriteOperations: flags.includes('write_operation') || flags.includes('possible_write_api_call'),
    containsShellCommands: flags.includes('shell_command'),
    containsFileOperations: flags.includes('file_operation'),
    containsPackageInstall: flags.includes('package_install'),
    containsProcessExecution: flags.includes('process_execution'),
    usesExternalApis: flags.includes('external_http_call'),
    requiresSecretsOrApiKeys: flags.includes('secret_or_key_reference'),
    hasEmbeddedTokenOrSecretCandidate: flags.includes('embedded_token_or_secret_candidate'),
    hasSecretPrintingRisk: flags.includes('secret_printing_risk'),
    isReadOnlyKqlCandidate: item.detectedKql.length > 0,
    hasNonReadOnlyKql: flags.includes('non_readonly_kql'),
    customerWorkspaceTokensEmbedded: flags.includes('customer_workspace_token_candidate'),
    detectedShellCommands: [],
    detectedWriteOperations: [],
    detectedFileOperations: [],
    detectedSecretIndicators: [],
    detectedAuthIndicators: [],
    detectedExternalApiCalls: item.detectedUrls ?? [],
    detectedExternalDomains: [],
    detectedNonReadOnlyKql: [],
    recommendedTrustStatus: 'unreviewed',
  };
}

function yesNo(value: boolean): string { return value ? 'Yes' : 'No'; }


type NotebookParamField = 'incident' | 'incidentTitle' | 'entity' | 'cve' | 'ioc' | 'campaign' | 'sourceRecord' | 'context';
type NotebookParamImportance = 'required' | 'recommended' | 'optional' | 'disabled';
type NotebookParameterProfile = {
  key: string;
  title: string;
  description: string;
  defaultLookback: string;
  defaultMaxRows: number;
  suggestedSourceModule: string;
  required: NotebookParamField[];
  recommended: NotebookParamField[];
  optional: NotebookParamField[];
  examples: string[];
};

const DEFAULT_NOTEBOOK_PARAMETER_PROFILE: NotebookParameterProfile = {
  key: 'generic',
  title: 'Generic notebook parameters',
  description: 'This notebook mainly needs customer/workspace, lookback and max rows. Unsupported target fields are disabled to avoid accidental irrelevant context.',
  defaultLookback: '24h',
  defaultMaxRows: 50,
  suggestedSourceModule: 'standalone',
  required: [],
  recommended: [],
  optional: ['sourceRecord', 'context'],
  examples: ['Select a Customer Master workspace, choose a lookback, then run headless or open Interactive Lab.'],
};

const NOTEBOOK_PARAMETER_PROFILES: Array<NotebookParameterProfile & { match: RegExp }> = [
  { match: /01-incident-guided-triage|incident guided triage/i, key: 'incident_guided_triage', title: 'Incident guided triage parameters', description: 'Use an incident number, ARM incident name, or provider incident ID for focused triage. Leave it blank to show recent incidents.', defaultLookback: '24h', defaultMaxRows: 50, suggestedSourceModule: 'incident', required: [], recommended: ['incident'], optional: ['incidentTitle', 'entity', 'ioc', 'sourceRecord', 'context'], examples: ['Incident: 1234 or incident ARM name', 'Entity: account / user@domain.com'] },
  { match: /02-incident-timeline|incident timeline reconstruction/i, key: 'incident_timeline', title: 'Incident timeline parameters', description: 'Reconstruct a timeline around an incident or entity. Incident ID is recommended; entity is useful when investigating one user/device/IP.', defaultLookback: '72h', defaultMaxRows: 100, suggestedSourceModule: 'incident', required: [], recommended: ['incident'], optional: ['incidentTitle', 'entity', 'sourceRecord', 'context'], examples: ['Incident: 1234', 'Entity: host / SERVER01'] },
  { match: /03-entity-360|entity 360 deep dive/i, key: 'entity_360', title: 'Entity 360 parameters', description: 'Enter the entity type and value to focus the notebook. Without an entity it runs broad discovery only.', defaultLookback: '24h', defaultMaxRows: 100, suggestedSourceModule: 'entity_360', required: ['entity'], recommended: [], optional: ['incident', 'sourceRecord', 'context'], examples: ['account / aniket@kloudynet.com', 'host / LAPTOP-12345', 'ip / 8.8.8.8', 'domain / example.com'] },
  { match: /04-sentinel-defender-correlation|defender correlation|securityalert \+ securityincident/i, key: 'sentinel_defender_correlation', title: 'Sentinel + Defender correlation parameters', description: 'Use an incident or entity to correlate Sentinel incidents, SecurityAlert, and Defender Advanced Hunting evidence.', defaultLookback: '24h', defaultMaxRows: 100, suggestedSourceModule: 'incident', required: [], recommended: ['incident'], optional: ['incidentTitle', 'entity', 'sourceRecord', 'context'], examples: ['Incident: 1234', 'Entity: account / user@domain.com'] },
  { match: /05-threat-intel|threat intel enrichment|ioc enrichment/i, key: 'threat_intel_enrichment', title: 'Threat intel enrichment parameters', description: 'Enter an IOC value such as IP, domain, URL or hash. Entity context is optional.', defaultLookback: '7d', defaultMaxRows: 100, suggestedSourceModule: 'threat_intel', required: ['ioc'], recommended: [], optional: ['entity', 'incident', 'sourceRecord', 'context'], examples: ['IOC: 1.2.3.4', 'IOC: malicious.example', 'IOC: SHA256 hash'] },
  { match: /06-cisa-kev|cisa kev correlation/i, key: 'cisa_kev', title: 'CISA KEV correlation parameters', description: 'Run broadly for all KEV exposure or enter a CVE ID to deep-dive one vulnerability.', defaultLookback: '30d', defaultMaxRows: 100, suggestedSourceModule: 'vulnerability_management', required: [], recommended: ['cve'], optional: ['entity', 'sourceRecord', 'context'], examples: ['CVE: CVE-2026-12345'] },
  { match: /07-cve-remediation|cve remediation intelligence/i, key: 'cve_remediation', title: 'CVE remediation intelligence parameters', description: 'Enter a CVE to generate a focused remediation intelligence view and draft Intune detection/remediation scripts. Leave blank for top exposed CVEs.', defaultLookback: '30d', defaultMaxRows: 100, suggestedSourceModule: 'vulnerability_management', required: [], recommended: ['cve'], optional: ['entity', 'sourceRecord', 'context'], examples: ['CVE: CVE-2026-12345', 'Entity: host / critical-server'] },
  { match: /08-defender-vulnerability|vulnerability prioritization/i, key: 'defender_vulnerability_prioritization', title: 'Defender vulnerability prioritization parameters', description: 'Prioritize Defender exposure. CVE is optional for a focused view; otherwise it ranks the top exposed vulnerabilities.', defaultLookback: '30d', defaultMaxRows: 100, suggestedSourceModule: 'vulnerability_management', required: [], recommended: ['cve'], optional: ['entity', 'sourceRecord', 'context'], examples: ['CVE: CVE-2026-12345', 'Entity: host / device-name'] },
  { match: /09-ransomware|ransomware precursor/i, key: 'ransomware_precursor', title: 'Ransomware precursor hunt parameters', description: 'Hunt broadly or focus on one user/device/IP. Campaign/context text can hold hypotheses or Hunt Agent details.', defaultLookback: '72h', defaultMaxRows: 100, suggestedSourceModule: 'huntops', required: [], recommended: [], optional: ['entity', 'campaign', 'sourceRecord', 'context'], examples: ['Entity: host / SERVER01', 'Campaign: ransomware-weekend-watch'] },
  { match: /10-identity-compromise|identity compromise/i, key: 'identity_compromise', title: 'Identity compromise hunt parameters', description: 'Focus on a user/account when available. Leave blank to hunt risky sign-in and identity compromise patterns.', defaultLookback: '72h', defaultMaxRows: 100, suggestedSourceModule: 'huntops', required: [], recommended: ['entity'], optional: ['campaign', 'sourceRecord', 'context'], examples: ['Entity type: account', 'Entity value: user@domain.com'] },
  { match: /11-c2|beaconing/i, key: 'c2_beaconing', title: 'C2 / beaconing hunt parameters', description: 'Hunt outbound periodicity and suspicious destinations. IOC/entity values are optional pivots.', defaultLookback: '72h', defaultMaxRows: 100, suggestedSourceModule: 'huntops', required: [], recommended: [], optional: ['ioc', 'entity', 'campaign', 'sourceRecord', 'context'], examples: ['IOC: suspicious.domain', 'Entity: host / endpoint01'] },
  { match: /12-sentinel-cost|cost optimization/i, key: 'sentinel_cost', title: 'Sentinel cost optimization parameters', description: 'Workspace/time scoped cost and noisy table analysis. Target fields are not required.', defaultLookback: '7d', defaultMaxRows: 100, suggestedSourceModule: 'advanced_hunting', required: [], recommended: [], optional: ['sourceRecord', 'context'], examples: ['Use 7d or 30d lookback for cost patterns.'] },
  { match: /13-ingestion-anomaly|ingestion anomaly/i, key: 'ingestion_anomaly', title: 'Ingestion anomaly parameters', description: 'Workspace/time scoped ingestion anomaly analysis. Target fields are not required.', defaultLookback: '7d', defaultMaxRows: 100, suggestedSourceModule: 'advanced_hunting', required: [], recommended: [], optional: ['sourceRecord', 'context'], examples: ['Use 24h for outage checks or 7d for trend checks.'] },
  { match: /14-connector-health|connector health investigation/i, key: 'connector_health', title: 'Connector health parameters', description: 'Workspace/time scoped connector freshness and health investigation. Source record may reference a connector finding.', defaultLookback: '7d', defaultMaxRows: 100, suggestedSourceModule: 'advanced_hunting', required: [], recommended: [], optional: ['sourceRecord', 'context'], examples: ['Source record: connector health finding ID'] },
  { match: /15-parser-quality|parser quality/i, key: 'parser_quality', title: 'Parser quality parameters', description: 'Workspace/time scoped parser quality checks. Target fields are not required.', defaultLookback: '24h', defaultMaxRows: 100, suggestedSourceModule: 'advanced_hunting', required: [], recommended: [], optional: ['sourceRecord', 'context'], examples: ['Use 24h for recent DCR/parser validation.'] },
  { match: /16-executive-soc|executive soc summary/i, key: 'executive_soc_summary', title: 'Executive SOC summary parameters', description: 'Customer-safe executive summary. Incident/entity values are optional filters.', defaultLookback: '7d', defaultMaxRows: 100, suggestedSourceModule: 'mission_control', required: [], recommended: [], optional: ['incident', 'entity', 'sourceRecord', 'context'], examples: ['Use 7d for weekly briefings or 30d for monthly reviews.'] },
  { match: /17-detection-quality|detection quality review/i, key: 'detection_quality', title: 'Detection quality parameters', description: 'Detection quality and alert-noise analysis. Incident/entity values are optional pivots.', defaultLookback: '30d', defaultMaxRows: 100, suggestedSourceModule: 'ml_insights', required: [], recommended: [], optional: ['incident', 'entity', 'sourceRecord', 'context'], examples: ['Use 30d to review false-positive and noisy detection trends.'] },
  { match: /18-sla-breach|sla breach prediction/i, key: 'sla_breach_prediction', title: 'SLA breach prediction parameters', description: 'SLA risk prediction and explanation. Incident ID is optional for a focused case.', defaultLookback: '7d', defaultMaxRows: 100, suggestedSourceModule: 'ml_insights', required: [], recommended: [], optional: ['incident', 'incidentTitle', 'entity', 'sourceRecord', 'context'], examples: ['Incident: 1234', 'Use 7d for current SLA risk.'] },
];

function getNotebookParameterProfile(item?: NotebookCatalogItem | null): NotebookParameterProfile {
  if (!item) return DEFAULT_NOTEBOOK_PARAMETER_PROFILE;
  const key = `${item.notebookPath || ''} ${item.notebookName || ''} ${item.title || ''}`;
  const match = NOTEBOOK_PARAMETER_PROFILES.find((profile) => profile.match.test(key));
  if (!match) return DEFAULT_NOTEBOOK_PARAMETER_PROFILE;
  const { match: _match, ...profile } = match;
  return profile;
}

function parameterImportance(profile: NotebookParameterProfile, field: NotebookParamField): NotebookParamImportance {
  if (profile.required.includes(field)) return 'required';
  if (profile.recommended.includes(field)) return 'recommended';
  if (profile.optional.includes(field)) return 'optional';
  return 'disabled';
}

function parameterEnabled(profile: NotebookParameterProfile, field: NotebookParamField): boolean {
  return parameterImportance(profile, field) !== 'disabled';
}

function parameterBadgeClass(importance: NotebookParamImportance): string {
  if (importance === 'required') return 'border-red-300 bg-red-50 text-red-700 dark:border-red-400/50 dark:bg-red-500/10 dark:text-red-100';
  if (importance === 'recommended') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400/50 dark:bg-amber-500/10 dark:text-amber-100';
  if (importance === 'optional') return 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-400/40 dark:bg-cyan-500/10 dark:text-cyan-100';
  return 'border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-500';
}

function parameterInputClass(enabled: boolean, importance?: NotebookParamImportance): string {
  const tone = importance === 'required'
    ? 'border-red-500/50 focus:border-red-300'
    : importance === 'recommended'
      ? 'border-amber-500/50 focus:border-amber-300'
      : 'border-slate-700 focus:border-cyan-300';
  return `rounded-lg border ${tone} bg-slate-950 px-3 py-2 text-xs text-white outline-none disabled:cursor-not-allowed disabled:border-slate-800 disabled:bg-slate-950/40 disabled:text-slate-600 disabled:placeholder:text-slate-700 ${enabled ? '' : 'opacity-60'}`;
}

function mergeParametersText(text: string, patch: Record<string, unknown>): string {
  let current: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(text || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) current = parsed as Record<string, unknown>;
  } catch {
    current = {};
  }
  return JSON.stringify({ ...current, ...patch }, null, 2);
}

function prettyMode(mode: string): string {
  const map: Record<string, string> = {
    preview_only: 'Preview only',
    parameterize_only: 'Parameterize only',
    run_readonly_kql: 'Run read-only KQL cells',
    run_trusted_notebook: 'Legacy trusted notebook',
    msticpy_headless: 'MSTICPy headless mode',
    run_best_effort_notebook: 'Full notebook best-effort mode',
    kloudsecure_native: 'KloudSecure-native notebook mode',
  };
  return map[mode] ?? mode.replace(/_/g, ' ');
}

function summaryText(value?: Record<string, unknown>): string {
  if (!value || Object.keys(value).length === 0) return 'No summary yet.';
  const parts: string[] = [];
  for (const [key, raw] of Object.entries(value).slice(0, 8)) {
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') parts.push(`${key}: ${raw}`);
    else if (Array.isArray(raw)) parts.push(`${key}: ${raw.length} item(s)`);
  }
  return parts.join(' · ') || JSON.stringify(value).slice(0, 400);
}

function objectEntries(value: unknown): Array<[string, unknown]> {
  return value && typeof value === 'object' && !Array.isArray(value) ? Object.entries(value as Record<string, unknown>) : [];
}

function artifactSummaryLabel(artifact: NotebookRunArtifact): string {
  if (artifact.artifactType === 'html_report') return 'Interactive HTML report';
  if (artifact.artifactType === 'summary_json') return 'Run summary';
  if (artifact.artifactType === 'preflight_json') return 'Preflight diagnostics';
  if (artifact.artifactType === 'execution_diagnostics_json') return 'Execution diagnostics';
  if (artifact.artifactType === 'kql_results_json') return 'KQL results';
  if (artifact.artifactType === 'support_files_json') return 'Support files';
  if (artifact.artifactType.includes('notebook')) return 'Notebook artifact';
  return artifact.artifactType.replace(/_/g, ' ');
}

function parseJsonArtifact(artifact: NotebookRunArtifact): unknown {
  if (artifact.contentJson !== undefined && artifact.contentJson !== null) return artifact.contentJson;
  if (!artifact.contentText) return null;
  try { return JSON.parse(artifact.contentText); } catch { return null; }
}

function JsonArtifactPreview({ artifact }: { artifact: NotebookRunArtifact }) {
  const parsed = parseJsonArtifact(artifact);
  const entries = objectEntries(parsed).slice(0, 10);
  return <div className="mt-3 space-y-3">
    {entries.length > 0 && <div className="grid gap-2 md:grid-cols-2">{entries.map(([key, value]) => <div key={key} className="rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase tracking-wide text-slate-500">{key}</div><div className="mt-1 max-h-24 overflow-auto text-xs text-slate-200">{Array.isArray(value) ? `${value.length} item(s)` : typeof value === 'object' && value !== null ? JSON.stringify(value).slice(0, 240) : String(value)}</div></div>)}</div>}
    <details className="rounded-xl border border-slate-800 bg-slate-950/60 p-3"><summary className="cursor-pointer text-xs font-semibold text-cyan-200">View raw JSON</summary><pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap text-[11px] text-slate-300">{JSON.stringify(parsed ?? artifact.contentText ?? {}, null, 2)}</pre></details>
  </div>;
}

function ArtifactPreview({ artifact }: { artifact: NotebookRunArtifact }) {
  const isHtml = artifact.mimeType === 'text/html' || artifact.artifactType === 'html_report' || artifact.filename.endsWith('.html');
  const isJson = artifact.mimeType === 'application/json' || artifact.filename.endsWith('.json') || artifact.artifactType.endsWith('_json');
  const isNotebook = artifact.filename.endsWith('.ipynb') || artifact.mimeType === 'application/x-ipynb+json';
  return <div className="rounded-2xl border border-slate-800 bg-black/20 p-3">
    <div className="flex flex-wrap items-start justify-between gap-2"><div><div className="text-sm font-semibold text-white">{artifactSummaryLabel(artifact)}</div><div className="text-xs text-slate-500">{artifact.filename} · {artifact.mimeType || 'artifact'}{artifact.sizeBytes ? ` · ${artifact.sizeBytes} bytes` : ''}</div></div><span className="rounded-full border border-slate-600 bg-slate-900 px-2 py-1 text-[10px] uppercase text-slate-300">{artifact.artifactType}</span></div>
    {isHtml && artifact.contentText && <div className="mt-3 overflow-hidden rounded-xl border border-cyan-500/20 bg-white"><iframe title={artifact.filename} sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={artifact.contentText} className="h-[620px] w-full bg-white" /></div>}
    {isJson && <JsonArtifactPreview artifact={artifact} />}
    {isNotebook && <div className="mt-3 rounded-xl border border-violet-500/20 bg-violet-500/10 p-3 text-xs text-violet-100">Notebook artifact generated. Use the HTML report and diagnostics for portal review; keep the executed/parameterized .ipynb for offline Jupyter analysis.</div>}
    {!isHtml && !isJson && !isNotebook && artifact.contentText && <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-xl border border-slate-800 bg-slate-950 p-3 text-[11px] text-slate-300">{artifact.contentText}</pre>}
  </div>;
}

function missingPipHints(check: DependencyCheckResponse | null): string[] {
  const status = check?.dependencies?.status;
  const hints = new Set<string>();
  for (const pkg of status?.recommendedPackages ?? []) if (pkg) hints.add(pkg);
  for (const pkg of check?.compatibility?.recommendedPackages ?? []) if (pkg) hints.add(pkg);
  for (const row of status?.importStatus ?? []) {
    if (row.available === false && typeof row.pipHint === 'string' && row.pipHint.trim()) {
      row.pipHint.trim().split(/\s+/).forEach((pkg) => hints.add(pkg));
    }
  }
  for (const pkg of status?.packageHints ?? []) if (pkg) hints.add(pkg);
  return [...hints].filter((pkg) => pkg !== 'mp_data').slice(0, 30);
}

function recommendedProfileIds(check: DependencyCheckResponse | null): string[] {
  const ids = new Set<string>();
  for (const id of check?.compatibility?.recommendedProfiles ?? []) if (id) ids.add(id);
  for (const id of check?.dependencies?.status?.recommendedProfiles ?? []) if (id) ids.add(id);
  return [...ids];
}

function BooleanCard({ label, value, danger }: { label: string; value: boolean; danger?: boolean }) {
  const classes = value
    ? danger ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-100' : 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100'
    : 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100';
  return <div className={`rounded-xl border p-3 ${classes}`}><div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div><div className="mt-1 text-sm font-bold">{yesNo(value)}</div></div>;
}

function DetailList({ title, items, empty, tone = 'slate' }: { title: string; items: string[]; empty: string; tone?: 'slate' | 'amber' | 'red' | 'cyan' }) {
  const toneMap = {
    slate: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-300',
    amber: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100',
    red: 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-100',
    cyan: 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-100',
  };
  return <div className={`rounded-xl border p-3 ${toneMap[tone]}`}><div className="text-xs font-bold uppercase tracking-wide opacity-80">{title}</div><div className="mt-2 max-h-40 overflow-auto space-y-1 text-xs">{items.length ? items.slice(0, 30).map((item, idx) => <div key={`${title}-${idx}`} className="rounded-lg bg-black/20 px-2 py-1 break-all">{item}</div>) : <div className="opacity-70">{empty}</div>}</div></div>;
}

async function loadCatalog(params: URLSearchParams): Promise<CatalogResponse> {
  return apiFetch<CatalogResponse>(`/notebooks/catalog?${params.toString()}`);
}

export function NotebookOpsTab() {
  const { canCapability } = useAuth();
  const canSync = canCapability('notebooks', 'sync');
  const canManageSources = canCapability('notebooks', 'manage_sources');
  const canPreview = canCapability('notebooks', 'preview');
  const canValidate = canCapability('notebooks', 'validate');
  const canManageTrust = canCapability('notebooks', 'manage_trust');
  const canDeveloperUnblock = canCapability('notebooks', 'developer_unblock');
  const canRunTrusted = canCapability('notebooks', 'run_trusted');
  const canRunReadonlyKql = canCapability('notebooks', 'run_readonly_kql');
  const canRunMsticpyHeadless = canCapability('notebooks', 'run_msticpy_headless');
  const canRunBestEffortNotebook = canCapability('notebooks', 'run_best_effort_notebook');
  const canRunNativeTemplates = canCapability('notebooks', 'run_native_templates');
  const canViewCustomNotebooks = canCapability('notebooks', 'custom_view') || canCapability('notebooks', 'read');
  const canCreateCustomNotebooks = canCapability('notebooks', 'custom_create') || canCapability('notebooks', 'interactive_lab_save');
  const canManageCustomNotebooks = canCapability('notebooks', 'custom_manage') || canCapability('notebooks', 'manage_sources');
  const canScheduleCustomNotebooks = canCapability('notebooks', 'custom_schedule');
  const canUseRuntimeContext = canCapability('notebooks', 'use_runtime_context');
  const canViewRuns = canCapability('notebooks', 'view_runs');
  const canCancelRuns = canCapability('notebooks', 'cancel_runs');
  const canDeleteHistory = canCapability('notebooks', 'delete_history');
  const canUseInteractiveLab = canCapability('notebooks', 'interactive_lab');
  const canStartInteractiveLab = canCapability('notebooks', 'interactive_lab_start');
  const canOpenInteractiveLab = canCapability('notebooks', 'interactive_lab_open');
  const canSaveInteractiveLab = canCapability('notebooks', 'interactive_lab_save');
  const canStopInteractiveLab = canCapability('notebooks', 'interactive_lab_stop');
  const canCleanupInteractiveLab = canCapability('notebooks', 'interactive_lab_cleanup');
  const canDeleteInteractiveLab = canCapability('notebooks', 'interactive_lab_delete');
  const canViewWorkerHealth = canCapability('notebooks', 'view_worker_health');
  const canInstallWorkerDependencies = canCapability('notebooks', 'install_worker_dependencies');
  const canViewRiskDetails = canCapability('notebooks', 'view_risk_details') || canManageTrust || canValidate;

  const [mode, setMode] = useState<ViewMode>('catalog');
  const [sources, setSources] = useState<NotebookSource[]>([]);
  const [githubAuthenticated, setGithubAuthenticated] = useState(false);
  const [syncContext, setSyncContext] = useState({ preferLocalRepo: true, allowGithubFallback: false, autoPullLocalRepo: false, localRepoRoot: '/opt/kloudsecure/cache' });
  const [guardrailPolicy, setGuardrailPolicy] = useState<NotebookGuardrailPolicy | null>(null);
  const [catalog, setCatalog] = useState<NotebookCatalogItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [summary, setSummary] = useState<CatalogResponse['summary']>();
  const [customTemplates, setCustomTemplates] = useState<CustomNotebookTemplate[]>([]);
  const [customSummary, setCustomSummary] = useState<CustomTemplatesResponse['summary']>({ total: 0, done: 0, pending: 0 });
  const [customSchedules, setCustomSchedules] = useState<CustomNotebookSchedule[]>([]);
  const [seedingCustomTemplates, setSeedingCustomTemplates] = useState(false);
  const [syncRuns, setSyncRuns] = useState<SyncRun[]>([]);
  const [runtimeCustomers, setRuntimeCustomers] = useState<NotebookRuntimeCustomer[]>([]);
  const [launchSources, setLaunchSources] = useState<NotebookLaunchSource[]>([]);
  const [runs, setRuns] = useState<NotebookRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<NotebookRun | null>(null);
  const [interactiveConfig, setInteractiveConfig] = useState<InteractiveConfig | null>(null);
  const [interactiveSessions, setInteractiveSessions] = useState<InteractiveSession[]>([]);
  const [selectedInteractiveSession, setSelectedInteractiveSession] = useState<InteractiveSession | null>(null);
  const [interactiveIframeUrl, setInteractiveIframeUrl] = useState<string>('');
  const [interactiveStatusFilter, setInteractiveStatusFilter] = useState('all');
  const [startingInteractive, setStartingInteractive] = useState(false);
  const [savingInteractive, setSavingInteractive] = useState(false);
  const [savingInteractiveCustom, setSavingInteractiveCustom] = useState(false);
  const [stoppingInteractive, setStoppingInteractive] = useState<string | null>(null);
  const [deletingInteractive, setDeletingInteractive] = useState<string | null>(null);
  const [workerHealth, setWorkerHealth] = useState<WorkerHealth | null>(null);
  const [workerReadiness, setWorkerReadiness] = useState<WorkerReadiness | null>(null);
  const [workerDependencies, setWorkerDependencies] = useState<WorkerDependencyStatus | null>(null);
  const [dependencyProfiles, setDependencyProfiles] = useState<WorkerDependencyProfile[]>([]);
  const [installLogs, setInstallLogs] = useState<WorkerInstallLog[]>([]);
  const [dependencyCheck, setDependencyCheck] = useState<DependencyCheckResponse | null>(null);
  const [checkingDependencies, setCheckingDependencies] = useState(false);
  const [installingDependencies, setInstallingDependencies] = useState(false);
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([]);
  const [runStatusFilter, setRunStatusFilter] = useState('all');
  const [runForm, setRunForm] = useState({
    executionMode: 'run_readonly_kql',
    customerId: '',
    customerName: '',
    tenantId: '',
    subscriptionId: '',
    resourceGroup: '',
    workspaceId: '',
    workspaceName: '',
    timeRange: '24h',
    maxRows: '100',
    sourceModule: 'standalone',
    sourceRecordId: '',
    incidentId: '',
    incidentTitle: '',
    entityType: '',
    entityValue: '',
    cveId: '',
    iocValue: '',
    campaignId: '',
    threatIntelContext: '',
    generateAiSummary: 'false',
    renderPlots: 'true',
    parametersText: '{\n  "lookback": "24h",\n  "generateAiSummary": false,\n  "renderPlots": true\n}'
  });
  const [runningNotebook, setRunningNotebook] = useState(false);
  const [selected, setSelected] = useState<NotebookCatalogItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [trustFilter, setTrustFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [customQ, setCustomQ] = useState('');
  const [customCategoryFilter, setCustomCategoryFilter] = useState('all');
  const [customTrustFilter, setCustomTrustFilter] = useState('all');
  const [customImportFilter, setCustomImportFilter] = useState('all');
  const [newSource, setNewSource] = useState({ name: '', repoUrl: '', branch: 'master', pathFilter: '' });
  const [trustNotes, setTrustNotes] = useState('');

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (sourceFilter !== 'all') p.set('sourceId', sourceFilter);
    if (riskFilter !== 'all') p.set('riskLevel', riskFilter);
    if (trustFilter !== 'all') p.set('trustedStatus', trustFilter);
    if (categoryFilter !== 'all') p.set('category', categoryFilter);
    return p;
  }, [q, sourceFilter, riskFilter, trustFilter, categoryFilter]);

  async function refreshSources() {
    const data = await apiFetch<SourcesResponse>('/notebooks/sources');
    setSources(data.sources ?? []);
    setGithubAuthenticated(data.githubAuthenticated === true);
    setSyncContext({
      preferLocalRepo: data.preferLocalRepo !== false,
      allowGithubFallback: data.allowGithubFallback === true,
      autoPullLocalRepo: data.autoPullLocalRepo === true,
      localRepoRoot: data.localRepoRoot || '/opt/kloudsecure/cache',
    });
  }

  async function refreshGuardrails() {
    try {
      const data = await apiFetch<{ ok: boolean; policy: NotebookGuardrailPolicy }>('/notebooks/guardrails');
      setGuardrailPolicy(data.policy);
    } catch {
      setGuardrailPolicy(null);
    }
  }

  async function refreshRuntimeContexts() {
    try {
      const data = await apiFetch<{ ok: boolean; customers: NotebookRuntimeCustomer[]; launchSources: NotebookLaunchSource[] }>('/notebooks/runtime-contexts');
      setRuntimeCustomers(data.customers ?? []);
      setLaunchSources(data.launchSources ?? []);
    } catch {
      setRuntimeCustomers([]);
      setLaunchSources([]);
    }
  }

  function applyRuntimeCustomer(customerId: string) {
    const customer = runtimeCustomers.find((c) => c.id === customerId);
    if (!customer) {
      setRunForm((p) => ({ ...p, customerId, customerName: '', tenantId: '', subscriptionId: '', resourceGroup: '', workspaceId: '', workspaceName: '' }));
      return;
    }
    setRunForm((p) => ({
      ...p,
      customerId: customer.id,
      customerName: customer.name ?? '',
      tenantId: customer.tenantId ?? '',
      subscriptionId: customer.subscriptionId ?? '',
      resourceGroup: customer.resourceGroup ?? '',
      workspaceId: customer.workspaceId ?? '',
      workspaceName: customer.workspaceName ?? '',
    }));
  }

  useEffect(() => {
    if (!selected) return;
    const profile = getNotebookParameterProfile(selected);
    setRunForm((p) => {
      const nextLookback = p.timeRange || profile.defaultLookback;
      const nextMaxRows = p.maxRows || String(profile.defaultMaxRows);
      return {
        ...p,
        timeRange: nextLookback,
        maxRows: nextMaxRows,
        sourceModule: p.sourceModule === 'standalone' ? profile.suggestedSourceModule : p.sourceModule,
        parametersText: mergeParametersText(p.parametersText, {
          lookback: nextLookback,
          maxRows: Number(nextMaxRows) || profile.defaultMaxRows,
          generateAiSummary: p.generateAiSummary === 'true',
          renderPlots: p.renderPlots !== 'false',
          notebookParameterProfile: profile.key,
        }),
      };
    });
  }, [selected?.id]);

  function buildNotebookParameters(): Record<string, unknown> {
    let parameters: Record<string, unknown> = {};
    if (runForm.parametersText.trim()) {
      const parsed = JSON.parse(runForm.parametersText);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) parameters = parsed as Record<string, unknown>;
    }
    const maxRows = Math.max(1, Math.min(1000, Number(runForm.maxRows) || selectedParamProfile.defaultMaxRows));
    const merged: Record<string, unknown> = {
      ...parameters,
      lookback: runForm.timeRange,
      timeRange: runForm.timeRange,
      maxRows,
      generateAiSummary: runForm.generateAiSummary === 'true',
      enableLlmExplanation: runForm.generateAiSummary === 'true',
      enableLLM: runForm.generateAiSummary === 'true',
      renderPlots: runForm.renderPlots !== 'false',
      notebookParameterProfile: selectedParamProfile.key,
      notebookParameterTitle: selectedParamProfile.title,
    };
    const add = (key: string, value: string) => { if (value.trim()) merged[key] = value.trim(); };
    add('incidentId', runForm.incidentId);
    add('incidentTitle', runForm.incidentTitle);
    add('entityType', runForm.entityType);
    add('entityValue', runForm.entityValue);
    add('cveId', runForm.cveId);
    add('iocValue', runForm.iocValue);
    add('campaignId', runForm.campaignId);
    add('sourceRecordId', runForm.sourceRecordId);
    add('threatIntelContext', runForm.threatIntelContext);
    return merged;
  }

  async function refreshCatalog() {
    setLoading(true);
    setError(null);
    try {
      const data = await loadCatalog(params);
      setCatalog(data.catalog ?? []);
      setSummary(data.summary);
      setCategories(data.categories ?? []);
      if (selected && !(data.catalog ?? []).some((n) => n.id === selected.id)) setSelected(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }


  async function refreshCustomTemplates() {
    if (!canViewCustomNotebooks) return;
    try {
      const data = await apiFetch<CustomTemplatesResponse>('/notebooks/custom/templates');
      setCustomTemplates(data.templates ?? []);
      setCustomSummary(data.summary ?? { total: 0, done: 0, pending: 0 });
    } catch {
      setCustomTemplates([]);
      setCustomSummary({ total: 0, done: 0, pending: 0 });
    }
  }

  async function refreshCustomSchedules() {
    if (!canScheduleCustomNotebooks) return;
    try {
      const data = await apiFetch<{ ok: boolean; schedules: CustomNotebookSchedule[] }>('/notebooks/custom/schedules');
      setCustomSchedules(data.schedules ?? []);
    } catch {
      setCustomSchedules([]);
    }
  }

  async function seedCustomTemplates() {
    if (!canManageCustomNotebooks) return;
    setSeedingCustomTemplates(true);
    try {
      await apiFetch('/notebooks/custom/templates/seed', { method: 'POST' });
      await Promise.all([refreshCustomTemplates(), refreshCatalog(), refreshSources()]);
      toast({ title: 'KloudSecure templates synced', description: 'Custom notebook templates were imported into the governed catalog.' });
    } catch (err) {
      toast({ title: 'Custom template sync failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setSeedingCustomTemplates(false);
    }
  }

  async function openCustomTemplate(template: CustomNotebookTemplate) {
    if (!template.catalogId) {
      toast({ title: 'Template not imported', description: 'Click Seed / Sync Templates first, then open the notebook again.', variant: 'destructive' });
      return;
    }
    const existing = catalog.find((item) => item.id === template.catalogId);
    if (existing) {
      setSelected(existing);
      setTrustNotes(existing.trustNotes ?? '');
      setMode('catalog');
      return;
    }
    try {
      const data = await apiFetch<{ ok: boolean; notebook: NotebookCatalogItem }>(`/notebooks/catalog/${template.catalogId}/preview`);
      setSelected(data.notebook);
      setTrustNotes(data.notebook.trustNotes ?? '');
      if (data.notebook.effectiveGuardrails) setGuardrailPolicy(data.notebook.effectiveGuardrails);
      await refreshCatalog();
      setMode('catalog');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Could not open custom notebook', description: message, variant: 'destructive' });
    }
  }


  async function refreshSyncRuns() {
    try {
      const data = await apiFetch<{ ok: boolean; runs: SyncRun[] }>('/notebooks/sync-runs');
      setSyncRuns(data.runs ?? []);
    } catch {
      setSyncRuns([]);
    }
  }

  async function refreshRuns() {
    if (!canViewRuns) return;
    try {
      const qs = new URLSearchParams();
      if (runStatusFilter !== 'all') qs.set('status', runStatusFilter);
      const data = await apiFetch<{ ok: boolean; runs: NotebookRun[] }>(`/notebooks/runs?${qs.toString()}`);
      setRuns(data.runs ?? []);
    } catch {
      setRuns([]);
    }
  }

  async function refreshInteractiveConfig() {
    if (!canUseInteractiveLab) return;
    try {
      const data = await apiFetch<{ ok: boolean; config: InteractiveConfig }>('/notebooks/interactive/config');
      setInteractiveConfig(data.config ?? null);
    } catch {
      setInteractiveConfig(null);
    }
  }

  async function refreshInteractiveSessions() {
    if (!canUseInteractiveLab) return;
    try {
      const qs = new URLSearchParams();
      if (interactiveStatusFilter !== 'all') qs.set('status', interactiveStatusFilter);
      const data = await apiFetch<{ ok: boolean; sessions: InteractiveSession[] }>(`/notebooks/interactive/sessions?${qs.toString()}`);
      setInteractiveSessions(data.sessions ?? []);
      if (selectedInteractiveSession) {
        const updated = (data.sessions ?? []).find((s) => s.sessionId === selectedInteractiveSession.sessionId);
        if (updated) setSelectedInteractiveSession(updated);
      }
    } catch {
      setInteractiveSessions([]);
    }
  }

  async function refreshWorkerHealth() {
    if (!canViewWorkerHealth) return;
    try {
      const data = await apiFetch<WorkerHealth>('/notebooks/worker/health');
      setWorkerHealth(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setWorkerHealth({ ok: false, enabled: true, status: 'unreachable', message });
    }
  }


  async function refreshWorkerReadiness() {
    if (!canViewWorkerHealth) return;
    try {
      const data = await apiFetch<WorkerReadiness>('/notebooks/worker/readiness');
      setWorkerReadiness(data);
    } catch {
      setWorkerReadiness(null);
    }
  }

  async function refreshWorkerDependencies() {
    if (!canViewWorkerHealth) return;
    try {
      const data = await apiFetch<{ ok: boolean; dependencies: WorkerDependencyStatus }>('/notebooks/worker/dependencies');
      setWorkerDependencies(data.dependencies ?? null);
      if (data.dependencies?.readiness) setWorkerReadiness({ ok: true, readiness: data.dependencies.readiness });
      if (data.dependencies?.status?.profiles?.length) setDependencyProfiles(data.dependencies.status.profiles);
      if (data.dependencies?.status?.lastInstallLogs?.length) setInstallLogs(data.dependencies.status.lastInstallLogs);
    } catch {
      setWorkerDependencies(null);
    }
  }

  async function refreshDependencyProfiles() {
    if (!canViewWorkerHealth) return;
    try {
      const data = await apiFetch<{ ok: boolean; profiles: WorkerDependencyProfile[] }>('/notebooks/worker/dependency-profiles');
      setDependencyProfiles(data.profiles ?? []);
    } catch {
      setDependencyProfiles([]);
    }
  }

  async function refreshInstallLogs() {
    if (!canViewWorkerHealth) return;
    try {
      const data = await apiFetch<{ ok: boolean; logs: WorkerInstallLog[] }>('/notebooks/worker/dependencies/install-logs');
      setInstallLogs(data.logs ?? []);
    } catch {
      setInstallLogs([]);
    }
  }

  async function checkSelectedDependencies() {
    if (!selected) return;
    setCheckingDependencies(true);
    try {
      const data = await apiFetch<DependencyCheckResponse>(`/notebooks/catalog/${selected.id}/dependencies/check`, { method: 'POST' });
      setDependencyCheck(data);
      setSelectedProfileIds(recommendedProfileIds(data));
      if (data.readiness) setWorkerReadiness({ ok: true, readiness: data.readiness });
      const missing = data.dependencies?.status?.missingImports?.length ?? 0;
      const support = data.supportFileSummary?.count ?? 0;
      const compat = data.compatibility?.compatibilityLabel || 'compatibility classified';
      toast({ title: 'Dependency preflight complete', description: `${missing} missing import(s), ${support} support file(s), ${compat}.` });
    } catch (err) {
      toast({ title: 'Dependency preflight failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setCheckingDependencies(false);
    }
  }

  async function installSelectedDependencies() {
    const packages = missingPipHints(dependencyCheck);
    const profileIds = selectedProfileIds.length ? selectedProfileIds : recommendedProfileIds(dependencyCheck);
    if (!packages.length && !profileIds.length) {
      toast({ title: 'No dependency profile selected', description: 'Missing local modules such as mp_data are handled by local support-file injection, not pip.' });
      return;
    }
    const profileText = profileIds.length ? `Profiles: ${profileIds.join(', ')}\n` : '';
    const pkgText = packages.length ? `Packages: ${packages.join(', ')}\n` : '';
    if (!window.confirm(`Install selected dependency profile/packages into the Notebook Worker venv?\n\n${profileText}${pkgText}\nThis does not run as root; NOTEBOOKOPS_ALLOW_DEPENDENCY_INSTALL must be enabled on the worker.`)) return;
    setInstallingDependencies(true);
    try {
      const data = await apiFetch<{ ok: boolean; result: Record<string, unknown> }>('/notebooks/worker/dependencies/install', { method: 'POST', body: JSON.stringify({ packages, profileIds }) });
      const installId = String((data.result as any)?.installId ?? 'install log created');
      toast({ title: 'Dependency install completed', description: `${installId} · ${String((data.result as any)?.status ?? 'done')}` });
      await refreshWorkerReadiness();
      await refreshWorkerDependencies();
      await refreshDependencyProfiles();
      await refreshInstallLogs();
      await checkSelectedDependencies();
    } catch (err) {
      toast({ title: 'Dependency install failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
      await refreshInstallLogs();
    } finally {
      setInstallingDependencies(false);
    }
  }

  useEffect(() => {
    void refreshSources().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    void refreshSyncRuns();
    void refreshGuardrails();
    void refreshRuntimeContexts();
    void refreshCustomTemplates();
    void refreshCustomSchedules();
    void refreshRuns();
    void refreshInteractiveConfig();
    void refreshInteractiveSessions();
    void refreshWorkerHealth();
    void refreshWorkerReadiness();
    void refreshWorkerDependencies();
    void refreshDependencyProfiles();
    void refreshInstallLogs();
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => { void refreshCatalog(); }, 250);
    return () => window.clearTimeout(t);
  }, [params.toString()]);

  useEffect(() => {
    void refreshRuns();
  }, [runStatusFilter, canViewRuns]);

  useEffect(() => {
    void refreshInteractiveSessions();
  }, [interactiveStatusFilter, canUseInteractiveLab]);

  useEffect(() => {
    if (!selectedInteractiveSession || !interactiveIframeUrl) return;
    const t = window.setInterval(() => {
      void apiFetch(`/notebooks/interactive/sessions/${selectedInteractiveSession.sessionId}/heartbeat`, { method: 'POST' }).catch(() => undefined);
    }, 60_000);
    return () => window.clearInterval(t);
  }, [selectedInteractiveSession?.sessionId, interactiveIframeUrl]);

  async function sync(sourceId?: string) {
    if (!canSync) return;
    setSyncing(sourceId ?? 'all');
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; results: Array<{ sourceName: string; imported: number; updated: number; skipped: number; errors: string[]; syncMode?: string; localRepoPath?: string | null }> }>('/notebooks/sync', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sourceId, limit: 120 }),
      });
      const imported = data.results.reduce((n, r) => n + (r.imported ?? 0), 0);
      const updated = data.results.reduce((n, r) => n + (r.updated ?? 0), 0);
      const failed = data.results.filter((r) => r.errors?.length).length;
      const modes = Array.from(new Set(data.results.map((r) => r.syncMode).filter(Boolean))).join(', ');
      toast({ title: 'Notebook sync completed', description: `${imported} imported, ${updated} updated${failed ? `, ${failed} source(s) with warnings` : ''}${modes ? ` · ${modes}` : ''}.` });
      await Promise.all([refreshSources(), refreshCatalog(), refreshSyncRuns(), refreshGuardrails()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'Notebook sync failed', description: message, variant: 'destructive' });
    } finally {
      setSyncing(null);
    }
  }

  async function addSource() {
    if (!canManageSources) return;
    try {
      await apiFetch('/notebooks/sources', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(newSource),
      });
      setNewSource({ name: '', repoUrl: '', branch: 'master', pathFilter: '' });
      await refreshSources();
      toast({ title: 'Notebook source added', description: 'The source is ready for catalog sync.' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Could not add source', description: message, variant: 'destructive' });
    }
  }

  async function toggleSource(source: NotebookSource) {
    if (!canManageSources) return;
    await apiFetch(`/notebooks/sources/${source.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ enabled: !source.enabled }),
    });
    await refreshSources();
  }

  async function deleteSource(source: NotebookSource) {
    if (!canManageSources) return;
    if (!window.confirm(`Soft-delete notebook source '${source.name}' and its catalog rows?`)) return;
    await apiFetch(`/notebooks/sources/${source.id}`, { method: 'DELETE' });
    await Promise.all([refreshSources(), refreshCatalog()]);
  }

  async function previewNotebook(item: NotebookCatalogItem) {
    setSelected(item);
    if (!canPreview) return;
    try {
      const data = await apiFetch<{ ok: boolean; notebook: NotebookCatalogItem }>(`/notebooks/catalog/${item.id}/preview`);
      setSelected(data.notebook);
      setTrustNotes(data.notebook.trustNotes ?? '');
      if (data.notebook.effectiveGuardrails) setGuardrailPolicy(data.notebook.effectiveGuardrails);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Preview failed', description: message, variant: 'destructive' });
    }
  }

  async function validateSelected() {
    if (!selected || !canValidate) return;
    try {
      const data = await apiFetch<{ ok: boolean; notebook: NotebookCatalogItem }>(`/notebooks/catalog/${selected.id}/validate`, { method: 'POST' });
      setSelected(data.notebook);
      if (data.notebook.effectiveGuardrails) setGuardrailPolicy(data.notebook.effectiveGuardrails);
      await refreshCatalog();
      toast({ title: 'Notebook revalidated', description: 'Phase 2 preview and risk classification were refreshed.' });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      toast({ title: 'Validation failed', description: message, variant: 'destructive' });
    }
  }

  async function setTrust(status: TrustedStatus) {
    if (!selected || !canManageTrust) return;
    try {
      const data = await apiFetch<{ ok: boolean; notebook: NotebookCatalogItem }>(`/notebooks/catalog/${selected.id}/trust`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ trustedStatus: status, notes: trustNotes }),
      });
      setSelected(data.notebook);
      await refreshCatalog();
      toast({ title: 'Trust policy updated', description: `${data.notebook.title} is now ${status}.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Trust update failed', description: message, variant: 'destructive' });
    }
  }

  async function developerUnblockSelected() {
    if (!selected || !canDeveloperUnblock) return;
    const currentRisk = riskOf(selected).riskLabel || prettyRisk(selected.riskLevel);
    const defaultNotes = trustNotes?.trim() || `Developer reviewed ${selected.title} and approved unblock of static scanner false positive. Previous risk: ${currentRisk}.`;
    const confirmed = window.confirm(`Developer unblock will mark this notebook as trusted and change its risk classification from '${currentRisk}' to 'Requires Admin Review'.\n\nUse this only after reviewing the notebook source and confirming there are no embedded customer tokens or destructive actions. Continue?`);
    if (!confirmed) return;
    try {
      const data = await apiFetch<{ ok: boolean; notebook: NotebookCatalogItem }>(`/notebooks/catalog/${selected.id}/developer-unblock`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ riskLevel: 'requires_admin_review', notes: defaultNotes }),
      });
      setSelected(data.notebook);
      setTrustNotes(data.notebook.trustNotes ?? defaultNotes);
      if (data.notebook.effectiveGuardrails) setGuardrailPolicy(data.notebook.effectiveGuardrails);
      await refreshCatalog();
      toast({ title: 'Notebook unblocked', description: `${data.notebook.title} is now trusted with developer override.` });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      toast({ title: 'Developer unblock failed', description: message, variant: 'destructive' });
    }
  }

  async function runSelectedNotebook() {
    if (!selected || !canRunTrusted) return;
    setRunningNotebook(true);
    try {
      const parameters = buildNotebookParameters();
      const data = await apiFetch<{ ok: boolean; run: NotebookRun }>(`/notebooks/catalog/${selected.id}/run`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          executionMode: runForm.executionMode,
          customerId: runForm.customerId.trim() || undefined,
          customerName: runForm.customerName.trim() || undefined,
          tenantId: runForm.tenantId.trim() || undefined,
          subscriptionId: runForm.subscriptionId.trim() || undefined,
          resourceGroup: runForm.resourceGroup.trim() || undefined,
          workspaceId: runForm.workspaceId.trim() || undefined,
          workspaceName: runForm.workspaceName.trim() || undefined,
          sourceModule: runForm.sourceModule,
          sourceRecordId: runForm.sourceRecordId.trim() || undefined,
          incidentId: runForm.incidentId.trim() || undefined,
          incidentTitle: runForm.incidentTitle.trim() || undefined,
          entityType: runForm.entityType.trim() || undefined,
          entityValue: runForm.entityValue.trim() || undefined,
          cveId: runForm.cveId.trim() || undefined,
          iocValue: runForm.iocValue.trim() || undefined,
          campaignId: runForm.campaignId.trim() || undefined,
          threatIntelContext: runForm.threatIntelContext.trim() || undefined,
          timeRange: runForm.timeRange,
          parameters,
        }),
      });
      setSelectedRun(data.run);
      setMode('runs');
      await refreshRuns();
      toast({ title: 'Notebook run submitted', description: `${selected.title} submitted to the Notebook Worker.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Notebook run blocked/failed', description: message, variant: 'destructive' });
    } finally {
      setRunningNotebook(false);
    }
  }

  function interactivePayloadForSelected(): Record<string, unknown> {
    const parameters = buildNotebookParameters();
    return {
      notebookId: selected?.id,
      customerId: runForm.customerId.trim() || undefined,
      customerName: runForm.customerName.trim() || undefined,
      tenantId: runForm.tenantId.trim() || undefined,
      subscriptionId: runForm.subscriptionId.trim() || undefined,
      resourceGroup: runForm.resourceGroup.trim() || undefined,
      workspaceId: runForm.workspaceId.trim() || undefined,
      workspaceName: runForm.workspaceName.trim() || undefined,
      sourceModule: runForm.sourceModule,
      sourceRecordId: runForm.sourceRecordId.trim() || undefined,
      incidentId: runForm.incidentId.trim() || undefined,
      incidentTitle: runForm.incidentTitle.trim() || undefined,
      entityType: runForm.entityType.trim() || undefined,
      entityValue: runForm.entityValue.trim() || undefined,
      cveId: runForm.cveId.trim() || undefined,
      iocValue: runForm.iocValue.trim() || undefined,
      campaignId: runForm.campaignId.trim() || undefined,
      threatIntelContext: runForm.threatIntelContext.trim() || undefined,
      timeRange: runForm.timeRange,
      timeoutMinutes: interactiveConfig?.maxMinutes ?? 240,
      parameters,
    };
  }

  async function startInteractiveSessionForSelected() {
    if (!selected || !canStartInteractiveLab) return;
    if (selected.trustedStatus !== 'trusted') {
      toast({ title: 'Trust required', description: 'Interactive Lab can only open notebooks marked trusted.', variant: 'destructive' });
      return;
    }
    setStartingInteractive(true);
    try {
      const data = await apiFetch<{ ok: boolean; session: InteractiveSession; iframeUrl: string }>('/notebooks/interactive/sessions', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(interactivePayloadForSelected()),
      });
      setSelectedInteractiveSession(data.session);
      setInteractiveIframeUrl(data.iframeUrl);
      setMode('interactive');
      await refreshInteractiveSessions();
      toast({ title: 'Interactive Lab started', description: `${selected.title} is opening with a live Jupyter kernel-backed session.` });
    } catch (err) {
      toast({ title: 'Interactive Lab start failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setStartingInteractive(false);
    }
  }

  async function openInteractiveSession(session: InteractiveSession) {
    if (!canOpenInteractiveLab) return;
    try {
      const data = await apiFetch<{ ok: boolean; session: InteractiveSession; iframeUrl: string }>(`/notebooks/interactive/sessions/${session.sessionId}/open`, { method: 'POST' });
      setSelectedInteractiveSession(data.session);
      setInteractiveIframeUrl(data.iframeUrl);
      setMode('interactive');
      await refreshInteractiveSessions();
      toast({ title: 'Interactive Lab opened', description: `${data.session.notebookTitle} is ready.` });
    } catch (err) {
      toast({ title: 'Could not open Interactive Lab', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    }
  }

  async function saveInteractiveSnapshot(session?: InteractiveSession | null) {
    const target = session ?? selectedInteractiveSession;
    if (!target || !canSaveInteractiveLab) return;
    setSavingInteractive(true);
    try {
      const data = await apiFetch<{ ok: boolean; snapshot: InteractiveSnapshot }>(`/notebooks/interactive/sessions/${target.sessionId}/save-snapshot`, { method: 'POST' });
      toast({ title: 'Interactive snapshot saved', description: `${data.snapshot.checksumSha256.slice(0, 12)} · ${formatBytes(data.snapshot.sizeBytes ?? 0)}` });
      await refreshInteractiveSessions();
      const detail = await apiFetch<{ ok: boolean; session: InteractiveSession }>(`/notebooks/interactive/sessions/${target.sessionId}`);
      setSelectedInteractiveSession(detail.session);
    } catch (err) {
      toast({ title: 'Snapshot failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setSavingInteractive(false);
    }
  }


  async function saveInteractiveAsCustom(session?: InteractiveSession | null) {
    const target = session ?? selectedInteractiveSession;
    if (!target || !canCreateCustomNotebooks) return;
    const title = window.prompt('Save this Interactive Lab session as a Custom KloudSecure Notebook:', target.notebookTitle || 'Custom Notebook');
    if (!title || !title.trim()) return;
    setSavingInteractiveCustom(true);
    try {
      const data = await apiFetch<{ ok: boolean; notebook: NotebookCatalogItem; path: string }>(`/notebooks/interactive/sessions/${target.sessionId}/save-as-custom`, {
        method: 'POST',
        body: JSON.stringify({ title: title.trim() }),
      });
      toast({ title: 'Saved as custom notebook', description: `${data.notebook.title} was added to the KloudSecure catalog as an unreviewed draft.` });
      await Promise.all([refreshCustomTemplates(), refreshCatalog(), refreshSources()]);
      setSelected(data.notebook);
      setMode('catalog');
    } catch (err) {
      toast({ title: 'Save as custom notebook failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setSavingInteractiveCustom(false);
    }
  }

  async function stopInteractiveSession(session: InteractiveSession) {
    if (!canStopInteractiveLab) return;
    if (!window.confirm(`Stop Interactive Lab session '${session.notebookTitle}'? Unsaved browser-side notebook changes must be saved in Jupyter first.`)) return;
    setStoppingInteractive(session.sessionId);
    try {
      const data = await apiFetch<{ ok: boolean; session: InteractiveSession }>(`/notebooks/interactive/sessions/${session.sessionId}/stop`, { method: 'POST' });
      if (selectedInteractiveSession?.sessionId === session.sessionId) {
        setSelectedInteractiveSession(data.session);
        setInteractiveIframeUrl('');
      }
      await refreshInteractiveSessions();
      toast({ title: 'Interactive Lab stopped', description: `${session.notebookTitle} is now ${data.session.status}.` });
    } catch (err) {
      toast({ title: 'Stop failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setStoppingInteractive(null);
    }
  }


  async function deleteInteractiveSession(session: InteractiveSession) {
    if (!canDeleteInteractiveLab) return;
    if (!window.confirm(`Soft delete Interactive Lab session '${session.notebookTitle}'? Running sessions will be stopped first. Notebook snapshots remain governed by retention.`)) return;
    setDeletingInteractive(session.sessionId);
    try {
      await apiFetch<{ ok: boolean; sessionId: string }>(`/notebooks/interactive/sessions/${session.sessionId}`, {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: 'manual_soft_delete_from_ui' }),
      });
      if (selectedInteractiveSession?.sessionId === session.sessionId) {
        setSelectedInteractiveSession(null);
        setInteractiveIframeUrl('');
      }
      await refreshInteractiveSessions();
      toast({ title: 'Interactive session deleted', description: `${session.notebookTitle} was soft-deleted from the session list.` });
    } catch (err) {
      toast({ title: 'Delete failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setDeletingInteractive(null);
    }
  }

  async function cleanupExpiredInteractiveSessions() {
    if (!canCleanupInteractiveLab) return;
    try {
      const data = await apiFetch<{ ok: boolean; expired: number; sessions: string[] }>('/notebooks/interactive/cleanup-expired', { method: 'POST' });
      await refreshInteractiveSessions();
      toast({ title: 'Interactive cleanup completed', description: `${data.expired} session(s) expired/stopped.` });
    } catch (err) {
      toast({ title: 'Cleanup failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    }
  }

  async function refreshRun(run: NotebookRun) {
    if (!canViewRuns) return;
    try {
      const data = await apiFetch<{ ok: boolean; run: NotebookRun }>(`/notebooks/runs/${run.id}/refresh`, { method: 'POST' });
      setSelectedRun(data.run);
      await refreshRuns();
      toast({ title: 'Run refreshed', description: `${data.run.status} · ${data.run.workerStatus ?? 'worker state updated'}` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Refresh failed', description: message, variant: 'destructive' });
    }
  }

  async function openRun(run: NotebookRun) {
    if (!canViewRuns) return;
    try {
      const data = await apiFetch<{ ok: boolean; run: NotebookRun }>(`/notebooks/runs/${run.id}`);
      setSelectedRun(data.run);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Could not open run', description: message, variant: 'destructive' });
    }
  }

  async function cancelRun(run: NotebookRun) {
    if (!canCancelRuns) return;
    try {
      const data = await apiFetch<{ ok: boolean; run: NotebookRun }>(`/notebooks/runs/${run.id}/cancel`, { method: 'POST' });
      setSelectedRun(data.run);
      await refreshRuns();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Cancel failed', description: message, variant: 'destructive' });
    }
  }

  async function deleteRun(run: NotebookRun) {
    if (!canDeleteHistory) return;
    if (!window.confirm(`Soft-delete notebook run '${run.notebookTitle}'?`)) return;
    await apiFetch(`/notebooks/runs/${run.id}`, { method: 'DELETE' });
    if (selectedRun?.id === run.id) setSelectedRun(null);
    await refreshRuns();
  }

  function exportCatalog() {
    const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), catalog }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kloudsecure-notebookops-catalog-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const selectedRisk = selected ? riskOf(selected) : null;
  const selectedParamProfile = useMemo(() => getNotebookParameterProfile(selected), [selected]);
  const customCategories = useMemo(() => Array.from(new Set(customTemplates.map((t) => t.category).filter(Boolean))).sort(), [customTemplates]);
  const customFilteredTemplates = useMemo(() => {
    const needle = customQ.trim().toLowerCase();
    return customTemplates.filter((template) => {
      const imported = template.status === 'done' && Boolean(template.catalogId);
      if (customImportFilter === 'imported' && !imported) return false;
      if (customImportFilter === 'pending' && imported) return false;
      if (customCategoryFilter !== 'all' && template.category !== customCategoryFilter) return false;
      if (customTrustFilter !== 'all' && (template.trustedStatus || 'unreviewed') !== customTrustFilter) return false;
      if (!needle) return true;
      const haystack = `${template.title} ${template.category} ${template.notebookPath} ${template.slug} ${template.trustedStatus || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [customTemplates, customQ, customCategoryFilter, customTrustFilter, customImportFilter]);
  const selectedEligibility = selected?.executionEligibility;
  const selectedGuardrails = selected?.effectiveGuardrails ?? guardrailPolicy;
  const modeCanRun =
    runForm.executionMode === 'run_readonly_kql' ? canRunReadonlyKql :
    runForm.executionMode === 'msticpy_headless' ? canRunMsticpyHeadless :
    runForm.executionMode === 'run_best_effort_notebook' ? canRunBestEffortNotebook :
    runForm.executionMode === 'kloudsecure_native' ? canRunNativeTemplates :
    canRunTrusted;
  const modeHelp: Record<string, string> = {
    preview_only: 'Safe: generates a report/parameterized artifact only; no notebook cells or KQL are executed.',
    parameterize_only: 'Safe: injects KloudSecure runtime context and produces a portable notebook artifact only.',
    run_readonly_kql: 'Production-safe: executes detected read-only KQL snippets only through Azure Monitor Query.',
    msticpy_headless: 'Recommended for Microsoft Sentinel notebooks: injects msticpyconfig.yaml, ws_name, start/end, timespan, and MSTICPy/MSTICNB bootstrap before execution.',
    run_best_effort_notebook: 'Experimental: executes public notebook cells in tolerant mode. Widgets, demo data and interactive auth may still produce warnings.',
    kloudsecure_native: 'Production-safe for KloudSecure-authored templates designed for headless execution.',
    run_trusted_notebook: 'Legacy mode: trusted full notebook execution. Prefer MSTICPy headless for Microsoft Sentinel notebooks.',
  };

  return (
    <div className="space-y-4 rounded-3xl bg-slate-950 p-5 text-slate-100">
      <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-4 shadow-xl">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.35em] text-cyan-300">NotebookOps</div>
            <h2 className="mt-1 text-xl font-bold text-white">KloudSecure Notebook Lab</h2>
            <p className="mt-1 max-w-4xl text-sm text-slate-300">
              Custom NotebookOps now combines Microsoft notebook sync with KloudSecure-native templates, governed trust, headless execution, Interactive Lab opening, runtime context injection, schedules and audit-ready outputs.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setMode('catalog')} className={`rounded-lg border px-3 py-2 text-sm ${mode === 'catalog' ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200'}`}>Catalog</button>
            {canViewCustomNotebooks && <button onClick={() => setMode('custom')} className={`rounded-lg border px-3 py-2 text-sm ${mode === 'custom' ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200'}`}>Custom Notebooks</button>}
            {canUseInteractiveLab && <button onClick={() => setMode('interactive')} className={`rounded-lg border px-3 py-2 text-sm ${mode === 'interactive' ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200'}`}>Interactive Lab</button>}
            <button onClick={() => setMode('runs')} className={`rounded-lg border px-3 py-2 text-sm ${mode === 'runs' ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200'}`}>Runs</button>
            <button onClick={() => setMode('sources')} className={`rounded-lg border px-3 py-2 text-sm ${mode === 'sources' ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200'}`}>Sources / Sync</button>
            <button onClick={() => setMode('governance')} className={`rounded-lg border px-3 py-2 text-sm ${mode === 'governance' ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200'}`}>Governance</button>
            {canSync && <button disabled={syncing !== null} onClick={() => void sync()} className="rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 py-2 text-sm font-semibold text-emerald-100 disabled:opacity-50">{syncing ? 'Syncing...' : 'Sync local sources'}</button>}
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-8">
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-3"><div className="text-xs uppercase text-slate-400">Catalog</div><div className="mt-1 text-2xl font-bold text-white">{summary?.total ?? catalog.length}</div></div>
          <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/10 p-3"><div className="text-xs uppercase text-fuchsia-300">Custom Done</div><div className="mt-1 text-2xl font-bold text-fuchsia-100">{customSummary.done}/{customSummary.total}</div></div>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3"><div className="text-xs uppercase text-emerald-300">Read-only KQL</div><div className="mt-1 text-2xl font-bold text-emerald-100">{summary?.readOnlyKql ?? 0}</div></div>
          <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3"><div className="text-xs uppercase text-cyan-300">Safe Preview</div><div className="mt-1 text-2xl font-bold text-cyan-100">{summary?.safePreview ?? 0}</div></div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"><div className="text-xs uppercase text-amber-300">Secrets/API</div><div className="mt-1 text-2xl font-bold text-amber-100">{summary?.requiresSecrets ?? 0}</div></div>
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3"><div className="text-xs uppercase text-red-300">Blocked/Unsafe</div><div className="mt-1 text-2xl font-bold text-red-100">{summary?.blocked ?? 0}</div></div>
          <div className={`rounded-xl border p-3 ${workerHealth?.ok === false ? 'border-red-500/30 bg-red-500/10' : 'border-violet-500/30 bg-violet-500/10'}`}><div className="text-xs uppercase text-violet-300">Notebook Worker</div><div className="mt-1 text-sm font-bold text-white">{workerHealth?.enabled === false ? 'Disabled' : workerHealth?.ok === false ? 'Unreachable' : 'Ready / check'}</div><div className="text-xs text-slate-300">{workerHealth?.workerUrl || '127.0.0.1:8092'}</div></div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3"><div className="text-xs uppercase text-slate-400">Sync Mode</div><div className="mt-1 text-sm font-bold text-white">{syncContext.preferLocalRepo ? 'Local VM mirror' : 'GitHub API'}</div><div className="text-xs text-slate-400">{syncContext.allowGithubFallback ? 'GitHub fallback allowed' : 'No API fallback'} · token {githubAuthenticated ? 'configured' : 'not needed'}</div></div>
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">{error}</div>}

      {mode === 'catalog' && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_560px]">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Notebook Catalog</h3>
                <p className="text-sm text-slate-400">Filter synced notebooks, inspect static risk classification, then run trusted read-only KQL notebooks against selected customer workspaces.</p>
              </div>
              <button onClick={exportCatalog} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100">Export JSON</button>
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-5">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title/path/table" className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none md:col-span-2" />
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All sources</option>{sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
              <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All risks</option><option value="safe_preview_only">Safe Preview Only</option><option value="read_only_kql_notebook">Read-only KQL Notebook</option><option value="requires_external_enrichment">Requires External Enrichment</option><option value="requires_secrets_or_api_keys">Requires Secrets / API Keys</option><option value="contains_shell_or_file_operations">Contains Shell / File Operations</option><option value="blocked_or_unsafe">Blocked / Unsafe</option></select>
              <select value={trustFilter} onChange={(e) => setTrustFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All trust</option><option value="unreviewed">Unreviewed</option><option value="trusted">Trusted</option><option value="restricted">Restricted</option><option value="blocked">Blocked</option></select>
            </div>
            <div className="mt-2">
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All categories</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-slate-800">
              <div className="grid grid-cols-[minmax(240px,1fr)_170px_130px_90px] gap-0 border-b border-slate-800 bg-slate-900/80 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                <div>Notebook</div><div>Risk</div><div>Trust</div><div>Tables</div>
              </div>
              <div className="max-h-[640px] overflow-auto divide-y divide-slate-800">
                {loading && <div className="p-4 text-sm text-slate-400">Loading catalog...</div>}
                {!loading && catalog.length === 0 && <div className="p-4 text-sm text-slate-400">No notebooks synced yet. Open Sources / Sync and run a catalog sync.</div>}
                {catalog.map((item) => {
                  const analysis = riskOf(item);
                  return <button key={item.id} onClick={() => void previewNotebook(item)} className={`grid w-full grid-cols-[minmax(240px,1fr)_170px_130px_90px] gap-0 px-3 py-3 text-left hover:bg-slate-900/80 ${selected?.id === item.id ? 'bg-cyan-500/10' : 'bg-slate-950/30'}`}>
                    <div className="pr-3">
                      <div className="font-semibold text-white">{item.title}</div>
                      <div className="mt-1 text-xs text-slate-400">{item.sourceName} / {item.notebookPath}</div>
                      <div className="mt-2 flex flex-wrap gap-1">{(item.tags ?? []).slice(0, 4).map((t) => pill(t, `${item.id}-${t}`))}</div>
                    </div>
                    <div><span className={`rounded-full border px-2 py-1 text-[11px] ${statusClass(item.riskLevel)}`}>{analysis.riskLabel || prettyRisk(item.riskLevel)}</span><div className="mt-1 text-[10px] text-slate-500">Score {analysis.riskScore}/100</div></div>
                    <div><span className={`rounded-full border px-2 py-1 text-[11px] capitalize ${statusClass(item.trustedStatus)}`}>{item.trustedStatus}</span></div>
                    <div className="text-sm font-semibold text-slate-200">{item.requiredTables?.length ?? 0}</div>
                  </button>;
                })}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            {!selected || !selectedRisk ? (
              <div className="flex min-h-[420px] items-center justify-center rounded-xl border border-dashed border-slate-700 text-center text-sm text-slate-400">Select a notebook to preview metadata, risk signals and trust controls.</div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-white">{selected.title}</h3>
                      <p className="mt-1 text-xs text-slate-400">{selected.sourceName} / {selected.notebookPath}</p>
                    </div>
                    {selected.sourceUrl && <a className="rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-200 hover:bg-slate-900" href={selected.sourceUrl} target="_blank" rel="noreferrer">Open source</a>}
                  </div>
                  <p className="mt-3 text-sm text-slate-300">{selected.description || 'No description found in notebook markdown.'}</p>
                  <div className="mt-3 flex flex-wrap gap-2"><span className={`rounded-full border px-2 py-1 text-xs ${statusClass(selected.riskLevel)}`}>{selectedRisk.riskLabel}</span><span className={`rounded-full border px-2 py-1 text-xs capitalize ${statusClass(selected.trustedStatus)}`}>{selected.trustedStatus}</span>{selected.category && pill(selected.category)}{pill(`Risk score ${selectedRisk.riskScore}/100`)}</div>
                </div>

                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[10px] uppercase text-slate-400">Last sync</div><div className="mt-1 text-sm font-semibold text-white">{shortDate(selected.lastSyncedAt)}</div></div>
                  <BooleanCard label="Interactive auth" value={selectedRisk.needsInteractiveAuthentication} danger />
                  <BooleanCard label="Write operations" value={selectedRisk.containsWriteOperations || selectedRisk.hasNonReadOnlyKql} danger />
                  <BooleanCard label="Shell commands" value={selectedRisk.containsShellCommands || selectedRisk.containsPackageInstall || selectedRisk.containsProcessExecution} danger />
                  <BooleanCard label="External APIs" value={selectedRisk.usesExternalApis} />
                  <BooleanCard label="Read-only KQL candidate" value={!selectedRisk.isReadOnlyKqlCandidate} />
                </div>

                {canViewRiskDetails && <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-amber-200">Risk classification reasons</div>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-100/90">{selectedRisk.reasons.map((reason, idx) => <li key={idx}>{reason}</li>)}</ul>
                  <div className="mt-2 text-xs text-amber-100/80">Recommended trust status: <span className="font-bold capitalize">{selectedRisk.recommendedTrustStatus}</span></div>
                </div>}

                {selectedEligibility && <div className={`rounded-xl border p-3 ${selectedEligibility.blockedByDefault ? 'border-red-500/30 bg-red-500/10 text-red-100' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'}`}>
                  <div className="text-xs font-bold uppercase tracking-wide">Guardrail decision for future runner</div>
                  <div className="mt-1 text-sm font-semibold">{selectedEligibility.blockedByDefault ? 'Blocked by default' : 'No default blocker detected'}</div>
                  {selectedEligibility.blockedReasons.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">{selectedEligibility.blockedReasons.map((reason, idx) => <li key={idx}>{reason}</li>)}</ul>}
                  {selectedEligibility.allowedOverrides.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-100">{selectedEligibility.allowedOverrides.map((reason, idx) => <li key={idx}>{reason}</li>)}</ul>}
                  <div className="mt-2 text-xs opacity-80">Notebook execution is still disabled in Phase 2. This is a preview-only decision record.</div>
                </div>}

                <div className="grid gap-3 md:grid-cols-2">
                  <DetailList title="Required Sentinel / Defender tables" items={selected.requiredTables} empty="None detected" tone="cyan" />
                  <DetailList title="Required Python packages" items={selected.requiredPackages} empty="No explicit Python package imports or installs detected" tone="slate" />
                  <DetailList title="External URLs / APIs" items={selectedRisk.detectedExternalApiCalls.length ? selectedRisk.detectedExternalApiCalls : selected.detectedUrls} empty="No external URL/API indicators detected" tone={selectedRisk.usesExternalApis ? 'amber' : 'slate'} />
                  <DetailList title="External domains" items={selectedRisk.detectedExternalDomains} empty="No external domains detected" tone="slate" />
                  <DetailList title="Authentication / secret indicators" items={[...selectedRisk.detectedAuthIndicators, ...selectedRisk.detectedSecretIndicators]} empty="No auth or secret indicators detected" tone={selectedRisk.requiresSecretsOrApiKeys ? 'red' : 'slate'} />
                  <DetailList title="Shell / file / write indicators" items={[...selectedRisk.detectedShellCommands, ...selectedRisk.detectedFileOperations, ...selectedRisk.detectedWriteOperations, ...selectedRisk.detectedNonReadOnlyKql]} empty="No shell, file or write indicators detected" tone={(selectedRisk.containsShellCommands || selectedRisk.containsFileOperations || selectedRisk.containsWriteOperations || selectedRisk.hasNonReadOnlyKql) ? 'red' : 'slate'} />
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                  <div className="text-xs uppercase text-slate-400">Markdown summary / preview</div>
                  <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap text-xs leading-relaxed text-slate-300">{selected.previewMarkdown || 'No markdown preview available.'}</pre>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                  <div className="text-xs uppercase text-slate-400">Detected KQL snippets</div>
                  <div className="mt-2 max-h-52 space-y-2 overflow-auto">
                    {selected.detectedKql.length ? selected.detectedKql.slice(0, 4).map((kql, idx) => <pre key={idx} className="rounded-lg bg-black/40 p-2 text-[11px] text-cyan-100 whitespace-pre-wrap">{kql}</pre>) : <div className="text-sm text-slate-500">No KQL snippet detected.</div>}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                  <div className="text-xs uppercase text-slate-400">Cell risk summary</div>
                  <div className="mt-2 max-h-44 overflow-auto space-y-1 text-xs">
                    {selected.cellSummary?.length ? selected.cellSummary.slice(0, 30).map((cell) => (
                      <div key={cell.index} className="rounded-lg border border-slate-800 bg-slate-950/50 p-2">
                        <span className="font-semibold text-slate-200">#{cell.index} {cell.type}</span><span className="ml-2 text-slate-400">{cell.firstLine || '-'}</span>
                        {cell.riskFlags?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{cell.riskFlags.map((f) => pill(f.replace(/_/g, ' '), `${cell.index}-${f}`))}</div>}
                      </div>
                    )) : <div className="text-slate-500">No cell summary available.</div>}
                  </div>
                </div>

                {canDeveloperUnblock && (selected.riskLevel === 'blocked_or_unsafe' || selected.riskLevel === 'blocked_or_admin_review' || selected.trustedStatus === 'blocked') && <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/10 p-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-fuchsia-200">Developer unblock</div>
                  <p className="mt-1 text-xs text-fuchsia-100/80">Use this only for KloudSecure-authored notebooks after source review when the static scanner marks notebook helper code, LLM calls, or generated-script text as unsafe. It changes the risk classification to <b>Requires Admin Review</b>, marks the notebook trusted, and writes an audit record. Possible embedded customer workspace tokens remain non-overridable.</p>
                  <button onClick={() => void developerUnblockSelected()} className="mt-3 rounded-lg border border-fuchsia-400/50 bg-fuchsia-500/20 px-3 py-2 text-xs font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30">Developer unblock + trust</button>
                </div>}

                <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                  <div className="text-xs uppercase text-slate-400">Trust policy</div>
                  <textarea value={trustNotes} onChange={(e) => setTrustNotes(e.target.value)} placeholder="Optional review notes" className="mt-2 min-h-20 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canValidate && <button onClick={() => void validateSelected()} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100">Re-validate</button>}
                    {canManageTrust && <><button onClick={() => void setTrust('trusted')} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100">Trust</button><button onClick={() => void setTrust('restricted')} className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100">Restrict</button><button onClick={() => void setTrust('blocked')} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-100">Block</button><button onClick={() => void setTrust('unreviewed')} className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100">Mark unreviewed</button></>}
                  </div>
                </div>

                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide text-emerald-200">Worker readiness, compatibility and dependency profiles</div>
                      <p className="mt-1 text-xs text-emerald-100/80">Run this before full notebook execution. It checks worker readiness, missing imports, support files, compatibility class and recommended profile installs.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canViewWorkerHealth && <button onClick={() => { void refreshWorkerReadiness(); void refreshWorkerDependencies(); void refreshDependencyProfiles(); void refreshInstallLogs(); }} className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100">Refresh readiness</button>}
                      <button disabled={checkingDependencies} onClick={() => void checkSelectedDependencies()} className="rounded-lg border border-emerald-400/50 bg-emerald-500/15 px-3 py-2 text-xs font-semibold text-emerald-100 disabled:opacity-50">{checkingDependencies ? 'Checking...' : 'Check dependencies'}</button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-3">
                    <div className={`rounded-xl border p-3 ${workerReadiness?.readiness?.overallStatus === 'not_ready' ? 'border-red-500/30 bg-red-500/10' : workerReadiness?.readiness?.overallStatus === 'ready_with_warnings' ? 'border-amber-500/30 bg-amber-500/10' : 'border-emerald-500/30 bg-emerald-500/10'}`}>
                      <div className="text-[10px] uppercase text-slate-300">Worker readiness</div>
                      <div className="mt-1 text-sm font-bold text-white">{workerReadiness?.readiness?.overallStatus?.replace(/_/g, ' ') || 'Not checked'}</div>
                      <div className="mt-1 text-xs text-slate-300">{workerReadiness?.readiness?.matrix?.filter((m) => m.status === 'fail').length ?? 0} fail · {workerReadiness?.readiness?.matrix?.filter((m) => m.status === 'warn').length ?? 0} warn</div>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase text-slate-400">Installer</div><div className="mt-1 text-sm font-bold text-white">{workerDependencies?.status?.installEnabled || dependencyCheck?.dependencies?.status?.installEnabled ? 'Enabled' : 'Disabled'}</div><div className="mt-1 text-xs text-slate-400">venv only · root: {(workerDependencies?.status?.installRunsAsRoot || dependencyCheck?.dependencies?.status?.installRunsAsRoot) ? 'yes' : 'no'}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase text-slate-400">Latest install</div><div className="mt-1 text-sm font-bold text-white">{installLogs[0]?.status || 'No install log'}</div><div className="mt-1 text-xs text-slate-400">{installLogs[0]?.installId || 'Install logs appear after profile install'}</div></div>
                  </div>

                  {workerReadiness?.readiness?.matrix?.length ? <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                    <div className="text-[10px] uppercase text-slate-400">Readiness matrix</div>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                      {workerReadiness.readiness.matrix.slice(0, 12).map((row) => <div key={row.id} className={`rounded-lg border px-2 py-1 text-xs ${row.status === 'fail' ? 'border-red-500/30 bg-red-500/10 text-red-100' : row.status === 'warn' ? 'border-amber-500/30 bg-amber-500/10 text-amber-100' : row.status === 'pass' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100' : 'border-slate-700 bg-slate-900 text-slate-300'}`}><b>{row.label}</b><br/><span className="opacity-80">{row.details || row.status}</span></div>)}
                    </div>
                  </div> : null}

                  {dependencyCheck ? <div className="mt-3 grid gap-2 md:grid-cols-3">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase text-slate-400">Compatibility</div><div className="mt-1 text-sm font-bold text-white">{dependencyCheck.compatibility?.compatibilityLabel || 'Classified'}</div><div className="mt-1 text-xs text-slate-400">{dependencyCheck.compatibility?.nextBestAction || 'Review supported execution modes.'}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase text-slate-400">Missing imports</div><div className="mt-1 text-sm font-bold text-white">{dependencyCheck.dependencies?.status?.missingImports?.length ?? 0}</div><div className="mt-1 text-xs text-slate-400">{(dependencyCheck.dependencies?.status?.missingImports ?? []).slice(0, 8).join(', ') || 'None'}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase text-slate-400">Support files</div><div className="mt-1 text-sm font-bold text-white">{String(dependencyCheck.supportFileSummary?.count ?? 0)}</div><div className="mt-1 text-xs text-slate-400">{String(dependencyCheck.supportFileSummary?.baseDir ?? dependencyCheck.supportFileSummary?.reason ?? 'local mirror check')}</div></div>
                    <div className="md:col-span-3 rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                      <div className="text-[10px] uppercase text-slate-400">Recommended dependency profiles</div>
                      <div className="mt-2 grid gap-2 md:grid-cols-2">
                        {(dependencyProfiles.length ? dependencyProfiles : (dependencyCheck.dependencies?.status?.profiles ?? [])).filter((profile) => recommendedProfileIds(dependencyCheck).includes(profile.id)).map((profile) => <label key={profile.id} className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-900/70 p-2 text-xs text-slate-200"><input type="checkbox" checked={selectedProfileIds.includes(profile.id)} onChange={(e) => setSelectedProfileIds((prev) => e.target.checked ? [...new Set([...prev, profile.id])] : prev.filter((id) => id !== profile.id))} className="mt-1"/><span><b>{profile.label}</b><br/><span className="text-slate-400">{profile.description || profile.packages?.join(', ')}</span></span></label>)}
                        {!recommendedProfileIds(dependencyCheck).length && <div className="text-xs text-slate-400">No profile recommended. Use package hints below if needed.</div>}
                      </div>
                    </div>
                    <div className="md:col-span-3 rounded-xl border border-slate-800 bg-slate-950/70 p-3"><div className="text-[10px] uppercase text-slate-400">Package hints</div><div className="mt-2 flex flex-wrap gap-1">{missingPipHints(dependencyCheck).length ? missingPipHints(dependencyCheck).map((pkg) => pill(pkg, `pkg-${pkg}`)) : <span className="text-xs text-slate-400">No pip package install needed. Local modules are handled by support-file injection.</span>}</div><div className="mt-2 flex flex-wrap gap-2">{canInstallWorkerDependencies && <button disabled={installingDependencies || (!missingPipHints(dependencyCheck).length && !selectedProfileIds.length)} onClick={() => void installSelectedDependencies()} className="rounded-lg border border-amber-400/50 bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-100 disabled:opacity-50">{installingDependencies ? 'Installing...' : 'Install selected profile/packages'}</button>}<span className="text-xs text-slate-400">Installs target the worker .venv, not OS/root Python. Preflight reruns automatically after install.</span></div></div>
                    {(dependencyCheck.compatibility?.warnings?.length || dependencyCheck.compatibility?.reasons?.length) ? <div className="md:col-span-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100"><b>Compatibility notes</b><ul className="mt-1 list-disc space-y-1 pl-4">{[...(dependencyCheck.compatibility?.reasons ?? []), ...(dependencyCheck.compatibility?.warnings ?? [])].slice(0, 10).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div> : null}
                  </div> : <div className="mt-2 text-xs text-emerald-100/70">Run dependency preflight before trusted full notebook execution.</div>}
                </div>

                <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide text-violet-200">Notebook Worker execution modes</div>
                      <p className="mt-1 text-xs text-violet-100/80">Only trusted notebooks can be submitted. Custom notebooks use the same Notebook Worker fields, now with a notebook-aware Run Parameters panel below.</p>
                    </div>
                    <div className="rounded-full border border-violet-400/40 bg-violet-500/10 px-2 py-1 text-[11px] font-semibold text-violet-100">{selectedParamProfile.title}</div>
                  </div>

                  <div className="mt-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-cyan-100">Run Parameters</div>
                        <p className="mt-1 text-xs text-cyan-100/80">{selectedParamProfile.description}</p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {selectedParamProfile.required.map((field) => <span key={`req-${field}`} className={`rounded-full border px-2 py-0.5 text-[10px] ${parameterBadgeClass('required')}`}>{field} required</span>)}
                        {selectedParamProfile.recommended.map((field) => <span key={`rec-${field}`} className={`rounded-full border px-2 py-0.5 text-[10px] ${parameterBadgeClass('recommended')}`}>{field} recommended</span>)}
                        {selectedParamProfile.required.length === 0 && selectedParamProfile.recommended.length === 0 && <span className="rounded-full border border-slate-600 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-300">workspace/time scoped</span>}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedParamProfile.examples.map((example) => <span key={example} className="rounded-full border border-slate-700 bg-slate-950/70 px-2 py-0.5 text-[10px] text-slate-300">{example}</span>)}
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    <label className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Run mode</span>
                      <select value={runForm.executionMode} onChange={(e) => setRunForm((p) => ({ ...p, executionMode: e.target.value }))} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white">
                        <option value="run_readonly_kql">Read-only KQL extraction</option>
                        <option value="msticpy_headless">MSTICPy headless mode</option>
                        <option value="kloudsecure_native">KloudSecure-native notebook mode</option>
                        <option value="parameterize_only">Parameterize only</option>
                        <option value="preview_only">Preview only</option>
                        <option value="run_best_effort_notebook">Full notebook best-effort mode</option>
                        <option value="run_trusted_notebook">Legacy trusted notebook mode</option>
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Lookback</span>
                      <select value={runForm.timeRange} onChange={(e) => setRunForm((p) => ({ ...p, timeRange: e.target.value, parametersText: mergeParametersText(p.parametersText, { lookback: e.target.value, timeRange: e.target.value }) }))} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white"><option value="1h">1h</option><option value="4h">4h</option><option value="8h">8h</option><option value="12h">12h</option><option value="24h">24h</option><option value="48h">48h</option><option value="72h">72h</option><option value="7d">7d</option><option value="14d">14d</option><option value="30d">30d</option></select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Customer / workspace</span>
                      <select disabled={!canUseRuntimeContext} value={runForm.customerId} onChange={(e) => applyRuntimeCustomer(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white disabled:opacity-50"><option value="">Select Customer Master workspace</option>{runtimeCustomers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.workspaceName || c.workspaceId || 'no workspace'}</option>)}</select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Source module</span>
                      <select disabled={!canUseRuntimeContext} value={runForm.sourceModule} onChange={(e) => setRunForm((p) => ({ ...p, sourceModule: e.target.value }))} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white disabled:opacity-50">{(launchSources.length ? launchSources : [{ id: 'standalone', label: 'Standalone NotebookOps run' }]).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select>
                    </label>
                    <label className="space-y-1 md:col-span-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Workspace ID</span>
                      <input value={runForm.workspaceId} onChange={(e) => setRunForm((p) => ({ ...p, workspaceId: e.target.value }))} placeholder="Workspace ID resolved from Customer Master" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" />
                    </label>
                    <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Workspace name</span><input value={runForm.workspaceName} onChange={(e) => setRunForm((p) => ({ ...p, workspaceName: e.target.value }))} placeholder="Workspace name" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" /></label>
                    <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Max rows</span><input type="number" min="1" max="1000" value={runForm.maxRows} onChange={(e) => setRunForm((p) => ({ ...p, maxRows: e.target.value, parametersText: mergeParametersText(p.parametersText, { maxRows: Number(e.target.value) || selectedParamProfile.defaultMaxRows }) }))} placeholder="Max rows" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" /></label>
                    <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Tenant ID</span><input value={runForm.tenantId} onChange={(e) => setRunForm((p) => ({ ...p, tenantId: e.target.value }))} placeholder="Tenant ID" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" /></label>
                    <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Subscription ID</span><input value={runForm.subscriptionId} onChange={(e) => setRunForm((p) => ({ ...p, subscriptionId: e.target.value }))} placeholder="Subscription ID" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" /></label>
                    <label className="space-y-1 md:col-span-2"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Resource group</span><input value={runForm.resourceGroup} onChange={(e) => setRunForm((p) => ({ ...p, resourceGroup: e.target.value }))} placeholder="Resource group" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" /></label>

                    {(['incident', 'incidentTitle', 'entity', 'cve', 'ioc', 'campaign', 'sourceRecord', 'context'] as NotebookParamField[]).map((field) => {
                      const importance = parameterImportance(selectedParamProfile, field);
                      const enabled = parameterEnabled(selectedParamProfile, field);
                      const label = field === 'incident' ? 'Incident Number / Incident ID' : field === 'incidentTitle' ? 'Incident title' : field === 'entity' ? 'Entity Value' : field === 'cve' ? 'CVE ID' : field === 'ioc' ? 'IOC value' : field === 'campaign' ? 'Hunt Agent campaign ID' : field === 'sourceRecord' ? 'Source record / finding ID' : 'Optional context';
                      const badge = <span className={`rounded-full border px-1.5 py-0.5 text-[9px] ${parameterBadgeClass(importance)}`}>{importance}</span>;
                      if (field === 'context') return <label key={field} className="space-y-1 md:col-span-2"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><textarea disabled={!enabled} value={runForm.threatIntelContext} onChange={(e) => setRunForm((p) => ({ ...p, threatIntelContext: e.target.value }))} placeholder="Optional threat intel / CVE / campaign context text. Secrets are redacted server-side." className={`${parameterInputClass(enabled, importance)} min-h-20 w-full`} /></label>;
                      if (field === 'entity') return <div key={field} className="grid gap-2 md:col-span-2 md:grid-cols-2"><label className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Entity type{badge}</span><input disabled={!enabled} value={runForm.entityType} onChange={(e) => setRunForm((p) => ({ ...p, entityType: e.target.value, sourceModule: p.sourceModule === 'standalone' ? 'entity_360' : p.sourceModule }))} placeholder="account, host, ip, domain, url, hash" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label><label className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Entity value{badge}</span><input disabled={!enabled} value={runForm.entityValue} onChange={(e) => setRunForm((p) => ({ ...p, entityValue: e.target.value }))} placeholder="user@domain.com, host01, 1.2.3.4" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label></div>;
                      if (field === 'incident') return <label key={field} className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><input disabled={!enabled} value={runForm.incidentId} onChange={(e) => setRunForm((p) => ({ ...p, incidentId: e.target.value, sourceModule: p.sourceModule === 'standalone' ? 'incident' : p.sourceModule }))} placeholder="Incident ID / ARM name / number" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label>;
                      if (field === 'incidentTitle') return <label key={field} className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><input disabled={!enabled} value={runForm.incidentTitle} onChange={(e) => setRunForm((p) => ({ ...p, incidentTitle: e.target.value }))} placeholder="Incident title" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label>;
                      if (field === 'cve') return <label key={field} className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><input disabled={!enabled} value={runForm.cveId} onChange={(e) => setRunForm((p) => ({ ...p, cveId: e.target.value, sourceModule: p.sourceModule === 'standalone' ? 'vulnerability_management' : p.sourceModule }))} placeholder="CVE-2026-12345" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label>;
                      if (field === 'ioc') return <label key={field} className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><input disabled={!enabled} value={runForm.iocValue} onChange={(e) => setRunForm((p) => ({ ...p, iocValue: e.target.value, sourceModule: p.sourceModule === 'standalone' ? 'threat_intel' : p.sourceModule }))} placeholder="domain / IP / URL / hash" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label>;
                      if (field === 'campaign') return <label key={field} className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><input disabled={!enabled} value={runForm.campaignId} onChange={(e) => setRunForm((p) => ({ ...p, campaignId: e.target.value, sourceModule: p.sourceModule === 'standalone' ? 'hunt_agent_campaign' : p.sourceModule }))} placeholder="Campaign ID" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label>;
                      return <label key={field} className="space-y-1"><span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{badge}</span><input disabled={!enabled} value={runForm.sourceRecordId} onChange={(e) => setRunForm((p) => ({ ...p, sourceRecordId: e.target.value }))} placeholder="Source record/finding ID" className={`w-full ${parameterInputClass(enabled, importance)}`} /></label>;
                    })}
                    <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">AI Summary</span><select value={runForm.generateAiSummary} onChange={(e) => setRunForm((p) => ({ ...p, generateAiSummary: e.target.value, parametersText: mergeParametersText(p.parametersText, { generateAiSummary: e.target.value === 'true', enableLlmExplanation: e.target.value === 'true', enableLLM: e.target.value === 'true' }) }))} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white"><option value="false">False - skip AI summary</option><option value="true">True - generate AI summary</option></select></label>
                    <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Plot render</span><select value={runForm.renderPlots} onChange={(e) => setRunForm((p) => ({ ...p, renderPlots: e.target.value, parametersText: mergeParametersText(p.parametersText, { renderPlots: e.target.value === 'true' }) }))} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white"><option value="true">True - render notebook visuals</option><option value="false">False - tables only</option></select></label>
                    <label className="space-y-1 md:col-span-2"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Advanced parameters JSON</span><textarea value={runForm.parametersText} onChange={(e) => setRunForm((p) => ({ ...p, parametersText: e.target.value }))} className="min-h-24 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white" /></label>
                  </div>
                  <div className="mt-2 rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-2 text-xs text-cyan-100">{modeHelp[runForm.executionMode] || 'NotebookOps controlled execution mode.'}<br/>KQL/MSTICPy access uses the Notebook Worker managed identity/default Azure credential. KloudSecure injects customer workspace context as parameters/config only; no workspace keys or tokens are embedded. Run Parameters are also written into kloudsecure_parameters.json for Interactive Lab sessions.</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button disabled={!canRunTrusted || !modeCanRun || runningNotebook || selected.trustedStatus !== 'trusted'} onClick={() => void runSelectedNotebook()} className="rounded-lg border border-violet-400/50 bg-violet-500/15 px-3 py-2 text-xs font-semibold text-violet-100 disabled:opacity-50">{runningNotebook ? 'Submitting...' : runForm.executionMode === 'run_readonly_kql' ? 'Run read-only KQL now' : runForm.executionMode === 'msticpy_headless' ? 'Run MSTICPy headless' : runForm.executionMode === 'run_best_effort_notebook' ? 'Run best-effort notebook' : 'Run with Notebook Worker'}</button>
                    {canUseInteractiveLab && <button disabled={!canStartInteractiveLab || startingInteractive || selected.trustedStatus !== 'trusted'} onClick={() => void startInteractiveSessionForSelected()} className="rounded-lg border border-cyan-400/50 bg-cyan-500/15 px-3 py-2 text-xs font-semibold text-cyan-100 disabled:opacity-50">{startingInteractive ? 'Starting lab...' : 'Open Interactive Lab'}</button>}
                    {canViewWorkerHealth && <button onClick={() => void refreshWorkerHealth()} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100">Check worker</button>}
                    {canViewWorkerHealth && <button onClick={() => void checkSelectedDependencies()} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100">Dependency preflight</button>}
                    {selected.trustedStatus !== 'trusted' && <span className="text-xs text-amber-200">Trust this notebook before execution.</span>}{!modeCanRun && <span className="text-xs text-red-200">Missing permission for {prettyMode(runForm.executionMode)}.</span>}{runForm.executionMode === 'msticpy_headless' && !runForm.workspaceId && <span className="text-xs text-amber-200">MSTICPy headless mode needs a complete Customer Master workspace context.</span>}{!canUseRuntimeContext && <span className="text-xs text-amber-200">Runtime context injection is disabled for this role.</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'custom' && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/10 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Custom KloudSecure Notebooks</h3>
                <p className="mt-1 max-w-4xl text-sm text-fuchsia-100/80">KloudSecure-native investigation, hunting, enrichment, vulnerability, cost, connector-health and executive notebooks. These templates are imported into the governed catalog, trusted, run headless, opened interactively, exported and attached to evidence through the existing NotebookOps runtime.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { void refreshCustomTemplates(); void refreshCustomSchedules(); }} className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-semibold text-slate-100">Refresh</button>
                {canManageCustomNotebooks && <button disabled={seedingCustomTemplates} onClick={() => void seedCustomTemplates()} className="rounded-lg border border-fuchsia-400/50 bg-fuchsia-500/15 px-3 py-2 text-sm font-semibold text-fuchsia-100 disabled:opacity-50">{seedingCustomTemplates ? 'Syncing...' : 'Seed / Sync Templates'}</button>}
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/10 p-3"><div className="text-xs uppercase text-fuchsia-200">Templates</div><div className="mt-1 text-2xl font-bold text-white">{customSummary.total}</div></div>
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3"><div className="text-xs uppercase text-emerald-200">Imported</div><div className="mt-1 text-2xl font-bold text-white">{customSummary.done}</div></div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"><div className="text-xs uppercase text-amber-200">Pending</div><div className="mt-1 text-2xl font-bold text-white">{customSummary.pending}</div></div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h4 className="font-bold text-white">Custom notebook explorer</h4>
                  <p className="text-sm text-slate-400">Search, filter and open KloudSecure-native notebooks directly from this tab. Imported notebooks open in the governed Catalog detail pane for trust review, headless run and Interactive Lab launch.</p>
                </div>
                <span className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2 py-1 text-xs text-fuchsia-100">{customSummary.done}/{customSummary.total} imported</span>
              </div>
              <div className="mt-4 grid gap-2 lg:grid-cols-[minmax(240px,1fr)_180px_160px_150px]">
                <input value={customQ} onChange={(e) => setCustomQ(e.target.value)} placeholder="Search custom notebooks by title, category, path..." className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
                <select value={customCategoryFilter} onChange={(e) => setCustomCategoryFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All categories</option>{customCategories.map((category) => <option key={category} value={category}>{category}</option>)}</select>
                <select value={customTrustFilter} onChange={(e) => setCustomTrustFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All trust</option><option value="trusted">Trusted</option><option value="unreviewed">Unreviewed</option><option value="restricted">Restricted</option><option value="blocked">Blocked</option></select>
                <select value={customImportFilter} onChange={(e) => setCustomImportFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All import states</option><option value="imported">Imported only</option><option value="pending">Pending only</option></select>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                <span>{customFilteredTemplates.length} visible of {customTemplates.length} template(s)</span>
                <button onClick={() => { setCustomQ(''); setCustomCategoryFilter('all'); setCustomTrustFilter('all'); setCustomImportFilter('all'); }} className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-slate-200">Clear filters</button>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl border border-slate-800">
                <div className="grid grid-cols-[70px_minmax(260px,1fr)_190px_120px_150px] gap-0 border-b border-slate-800 bg-slate-900/80 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                  <div>Priority</div><div>Notebook</div><div>Category</div><div>Trust</div><div>Action</div>
                </div>
                <div className="max-h-[620px] overflow-auto divide-y divide-slate-800">
                  {customTemplates.length === 0 && <div className="p-4 text-sm text-slate-400">No custom template status loaded yet. Click refresh or seed the templates.</div>}
                  {customTemplates.length > 0 && customFilteredTemplates.length === 0 && <div className="p-4 text-sm text-slate-400">No custom notebooks match the current search/filter.</div>}
                  {customFilteredTemplates.map((template) => {
                    const imported = template.status === 'done' && template.catalogId;
                    return <div key={template.slug} className={`grid grid-cols-[70px_minmax(260px,1fr)_190px_120px_150px] items-center px-3 py-3 text-sm ${imported ? 'bg-slate-950/40 hover:bg-slate-900/80' : 'bg-slate-950/20 opacity-80'}`}>
                      <button disabled={!imported} onClick={() => void openCustomTemplate(template)} className="text-left font-bold text-slate-300 disabled:cursor-not-allowed">#{template.priority}</button>
                      <button disabled={!imported} onClick={() => void openCustomTemplate(template)} className="pr-3 text-left disabled:cursor-not-allowed"><div className="font-semibold text-white">{template.title}</div><div className="mt-1 text-xs text-slate-500">{template.notebookPath}</div></button>
                      <div className="text-xs text-slate-300">{template.category}</div>
                      <div className="text-xs capitalize text-slate-300">{template.trustedStatus || '-'}</div>
                      <div className="flex flex-wrap gap-1">
                        <button disabled={!imported} onClick={() => void openCustomTemplate(template)} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-2 py-1 text-xs font-semibold text-cyan-100 disabled:cursor-not-allowed disabled:opacity-40">Open</button>
                        {!imported && <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-100">Seed first</span>}
                      </div>
                    </div>;
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <h4 className="font-bold text-white">Lifecycle coverage</h4>
                <div className="mt-3 space-y-2 text-sm text-slate-300">
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3"><b className="text-emerald-100">Catalog + trust:</b> templates seed into the existing governed catalog and use existing trust controls.</div>
                  <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3"><b className="text-cyan-100">Interactive:</b> trusted custom notebooks open in the existing Jupyter-backed Interactive Lab.</div>
                  <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-3"><b className="text-violet-100">Headless:</b> trusted custom notebooks use the existing Notebook Worker execution modes.</div>
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"><b className="text-amber-100">Scheduling:</b> schedule definitions are stored and audited; executor wiring is the next hardening step.</div>
                  <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3"><b className="text-white">Save from lab:</b> an active Interactive Lab session can be saved back as an unreviewed custom notebook draft.</div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h4 className="font-bold text-white">Custom schedules</h4>
                    <p className="text-xs text-slate-400">Stored schedule definitions for trusted custom notebooks.</p>
                  </div>
                  <button onClick={() => void refreshCustomSchedules()} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100">Refresh</button>
                </div>
                <div className="mt-3 max-h-72 space-y-2 overflow-auto text-xs">
                  {customSchedules.length === 0 && <div className="rounded-xl border border-dashed border-slate-700 p-3 text-slate-400">No custom notebook schedules yet.</div>}
                  {customSchedules.map((schedule) => <div key={schedule.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"><div className="flex items-start justify-between gap-2"><div><div className="font-semibold text-white">{schedule.scheduleName}</div><div className="mt-1 text-slate-400">{schedule.notebookTitle}</div></div><span className={`rounded-full border px-2 py-0.5 ${statusClass(schedule.enabled ? 'enabled' : 'disabled')}`}>{schedule.enabled ? 'Enabled' : 'Disabled'}</span></div><div className="mt-2 text-slate-500">{schedule.cadence} · {schedule.customerName || schedule.workspaceName || 'No workspace'} · {shortDate(schedule.updatedAt || schedule.createdAt)}</div></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === 'interactive' && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Interactive Lab</h3>
                <p className="mt-1 max-w-4xl text-sm text-cyan-100/80">Live Jupyter kernel-backed sessions for trusted notebooks that need widgets, Bokeh output, manual selections and notebook-native interactivity. Sessions are localhost-bound, tokenized, runtime-context injected, audited and auto-expired.</p>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {interactiveConfig ? <>
                    {pill(`root ${interactiveConfig.workspaceRoot}`)}
                    {pill(`idle ${interactiveConfig.idleMinutes}m`)}
                    {pill(`max ${interactiveConfig.maxMinutes}m`)}
                    {pill(interactiveConfig.refusesRoot ? `non-root ${interactiveConfig.runAsUser}` : 'root allowed by env')}
                  </> : <span className="text-cyan-100/70">Interactive config not loaded or permission missing.</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <select value={interactiveStatusFilter} onChange={(e) => setInteractiveStatusFilter(e.target.value)} className="rounded-lg border border-cyan-500/30 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All sessions</option><option value="active">Active</option><option value="starting">Starting</option><option value="stopped">Stopped</option><option value="expired">Expired</option><option value="failed">Failed</option></select>
                <button onClick={() => { void refreshInteractiveConfig(); void refreshInteractiveSessions(); }} className="rounded-lg border border-cyan-400/50 bg-cyan-500/15 px-3 py-2 text-sm font-semibold text-cyan-100">Refresh</button>
                {canCleanupInteractiveLab && <button onClick={() => void cleanupExpiredInteractiveSessions()} className="rounded-lg border border-amber-400/50 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-100">Cleanup expired</button>}
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h4 className="font-bold text-white">Sessions</h4>
                  <p className="text-xs text-slate-400">Start from the Catalog pane, then open/stop/save snapshots here.</p>
                </div>
                <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-xs text-cyan-100">{interactiveSessions.filter((s) => s.status === 'active').length} active</span>
              </div>
              <div className="mt-3 max-h-[760px] space-y-2 overflow-auto">
                {interactiveSessions.length === 0 && <div className="rounded-xl border border-dashed border-slate-700 p-4 text-sm text-slate-400">No Interactive Lab sessions yet. Select a trusted notebook in Catalog and click Open Interactive Lab.</div>}
                {interactiveSessions.map((session) => (
                  <div key={session.sessionId} className={`rounded-xl border p-3 ${selectedInteractiveSession?.sessionId === session.sessionId ? 'border-cyan-400/50 bg-cyan-500/10' : 'border-slate-800 bg-slate-900/50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-white">{session.notebookTitle}</div>
                        <div className="mt-1 text-xs text-slate-400">{session.customerName || session.workspaceName || session.workspaceId || 'No workspace context'} · {shortDate(session.createdAt)}</div>
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] ${statusClass(session.status)}`}>{session.status}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-slate-400">
                      <div>Port: <span className="text-slate-200">{session.jupyterPort || '-'}</span></div>
                      <div>PID: <span className="text-slate-200">{session.jupyterPid || '-'}</span></div>
                      <div>Last: <span className="text-slate-200">{shortDate(session.lastActivityAt)}</span></div>
                      <div>Expires: <span className="text-slate-200">{shortDate(session.expiresAt)}</span></div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {canOpenInteractiveLab && <button disabled={!['active','starting'].includes(session.status)} onClick={() => void openInteractiveSession(session)} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-100 disabled:opacity-50">Open</button>}
                      {canSaveInteractiveLab && <button disabled={!['active','starting'].includes(session.status) || savingInteractive} onClick={() => void saveInteractiveSnapshot(session)} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-100 disabled:opacity-50">Snapshot</button>}
                      {canCreateCustomNotebooks && <button disabled={!['active','starting'].includes(session.status) || savingInteractiveCustom} onClick={() => void saveInteractiveAsCustom(session)} className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-1.5 text-xs font-semibold text-fuchsia-100 disabled:opacity-50">Save custom</button>}
                      {canStopInteractiveLab && <button disabled={!['active','starting'].includes(session.status) || stoppingInteractive === session.sessionId} onClick={() => void stopInteractiveSession(session)} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-100 disabled:opacity-50">{stoppingInteractive === session.sessionId ? 'Stopping...' : 'Stop'}</button>}
                      {canDeleteInteractiveLab && <button disabled={deletingInteractive === session.sessionId} onClick={() => void deleteInteractiveSession(session)} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-100 disabled:opacity-50">{deletingInteractive === session.sessionId ? 'Deleting...' : 'Delete'}</button>}
                    </div>
                    {session.stopReason && <div className="mt-2 text-xs text-amber-200">{session.stopReason}</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
              {selectedInteractiveSession ? (
                <div className="space-y-3">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h4 className="text-lg font-bold text-white">{selectedInteractiveSession.notebookTitle}</h4>
                      <p className="text-xs text-slate-400">{selectedInteractiveSession.notebookPath} · session {selectedInteractiveSession.sessionId}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canOpenInteractiveLab && <button disabled={!['active','starting'].includes(selectedInteractiveSession.status)} onClick={() => void openInteractiveSession(selectedInteractiveSession)} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 disabled:opacity-50">Reload iframe</button>}
                      {canSaveInteractiveLab && <button disabled={savingInteractive || !['active','starting'].includes(selectedInteractiveSession.status)} onClick={() => void saveInteractiveSnapshot()} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100 disabled:opacity-50">{savingInteractive ? 'Saving...' : 'Save snapshot'}</button>}
                      {canCreateCustomNotebooks && <button disabled={savingInteractiveCustom || !['active','starting'].includes(selectedInteractiveSession.status)} onClick={() => void saveInteractiveAsCustom()} className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2 text-xs font-semibold text-fuchsia-100 disabled:opacity-50">{savingInteractiveCustom ? 'Saving...' : 'Save as custom'}</button>}
                      {canStopInteractiveLab && <button disabled={!['active','starting'].includes(selectedInteractiveSession.status)} onClick={() => void stopInteractiveSession(selectedInteractiveSession)} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-100 disabled:opacity-50">Stop session</button>}
                      {canDeleteInteractiveLab && <button disabled={deletingInteractive === selectedInteractiveSession.sessionId} onClick={() => void deleteInteractiveSession(selectedInteractiveSession)} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-100 disabled:opacity-50">{deletingInteractive === selectedInteractiveSession.sessionId ? 'Deleting...' : 'Delete session'}</button>}
                    </div>
                  </div>

                  <div className="grid gap-2 md:grid-cols-4">
                    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"><div className="text-[10px] uppercase text-slate-400">Status</div><div className="mt-1 text-sm font-bold text-white">{selectedInteractiveSession.status}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"><div className="text-[10px] uppercase text-slate-400">Workspace</div><div className="mt-1 truncate text-sm font-bold text-white">{selectedInteractiveSession.workspaceName || selectedInteractiveSession.workspaceId || '-'}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"><div className="text-[10px] uppercase text-slate-400">Last activity</div><div className="mt-1 text-sm font-bold text-white">{shortDate(selectedInteractiveSession.lastActivityAt)}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"><div className="text-[10px] uppercase text-slate-400">Expires</div><div className="mt-1 text-sm font-bold text-white">{shortDate(selectedInteractiveSession.expiresAt)}</div></div>
                  </div>

                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">Before clicking KloudSecure Save snapshot, use Jupyter's own Save action inside the iframe so browser-side edits are flushed to the .ipynb file. KloudSecure snapshots then copy the saved notebook and audit the checksum.</div>

                  {interactiveIframeUrl && ['active','starting'].includes(selectedInteractiveSession.status) ? (
                    <iframe title="KloudSecure NotebookOps Interactive Lab" src={interactiveIframeUrl} className="h-[760px] w-full rounded-xl border border-slate-700 bg-white" />
                  ) : (
                    <div className="flex h-[560px] items-center justify-center rounded-xl border border-dashed border-slate-700 text-center text-sm text-slate-400">Open an active session to load the live JupyterLab iframe.</div>
                  )}

                  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                    <div className="font-semibold text-white">Snapshots</div>
                    <div className="mt-2 max-h-40 space-y-1 overflow-auto text-xs">
                      {selectedInteractiveSession.snapshots?.length ? selectedInteractiveSession.snapshots.map((snapshot) => <div key={snapshot.id} className="rounded-lg border border-slate-800 bg-slate-950/50 p-2"><span className="text-slate-200">{shortDate(snapshot.savedAt)}</span><span className="ml-2 text-slate-500">{formatBytes(snapshot.sizeBytes ?? 0)} · {snapshot.checksumSha256.slice(0, 16)}</span></div>) : <div className="text-slate-500">No snapshots saved yet.</div>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[620px] items-center justify-center rounded-xl border border-dashed border-slate-700 text-center text-sm text-slate-400">Select or start an Interactive Lab session.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {mode === 'runs' && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_560px]">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Notebook Run History</h3>
                <p className="text-sm text-slate-400">Phase 3 jobs submitted to the separate Python Notebook Worker.</p>
              </div>
              <div className="flex gap-2">
                <select value={runStatusFilter} onChange={(e) => setRunStatusFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"><option value="all">All status</option><option value="queued">Queued</option><option value="running">Running</option><option value="completed">Completed</option><option value="failed">Failed</option><option value="cancelled">Cancelled</option></select>
                <button onClick={() => void refreshRuns()} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100">Refresh</button>
              </div>
            </div>
            <div className="mt-3 divide-y divide-slate-800 rounded-xl border border-slate-800">
              {runs.length === 0 && <div className="p-4 text-sm text-slate-400">No notebook runs yet.</div>}
              {runs.map((run) => <button key={run.id} onClick={() => void openRun(run)} className={`block w-full p-3 text-left hover:bg-slate-900/80 ${selectedRun?.id === run.id ? 'bg-violet-500/10' : 'bg-slate-950/30'}`}>
                <div className="flex items-start justify-between gap-3"><div><div className="font-semibold text-white">{run.notebookTitle}</div><div className="text-xs text-slate-400">{prettyMode(run.executionMode)} · {run.customerName || run.workspaceName || run.workspaceId || 'no workspace'}{run.sourceModule ? ` · ${run.sourceModule}` : ''} · {shortDate(run.createdAt)}</div></div><span className={`rounded-full border px-2 py-1 text-xs ${statusClass(run.status)}`}>{run.status}</span></div>
                {run.errorMessage && <div className="mt-2 text-xs text-red-300">{run.errorMessage}</div>}
              </button>)}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            {!selectedRun ? <div className="flex min-h-[360px] items-center justify-center rounded-xl border border-dashed border-slate-700 text-center text-sm text-slate-400">Select a notebook run to inspect status, artifacts and worker output.</div> : <div className="space-y-4">
              <div>
                <div className="flex items-start justify-between gap-3"><div><h3 className="text-lg font-bold text-white">{selectedRun.notebookTitle}</h3><p className="mt-1 text-xs text-slate-400">{selectedRun.sourceName} / {selectedRun.notebookPath}</p></div><span className={`rounded-full border px-2 py-1 text-xs ${statusClass(selectedRun.status)}`}>{selectedRun.status}</span></div>
                <div className="mt-3 flex flex-wrap gap-2">{pill(prettyMode(selectedRun.executionMode))}{selectedRun.workerStatus && pill(`Worker ${selectedRun.workerStatus}`)}{selectedRun.timeRange && pill(`Range ${selectedRun.timeRange}`)}{selectedRun.riskLevel && pill(prettyRisk(selectedRun.riskLevel))}{selectedRun.sourceModule && pill(`Source ${selectedRun.sourceModule}`)}{selectedRun.customerName && pill(selectedRun.customerName)}</div>
              </div>
              <div className="grid gap-2 md:grid-cols-2"><div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[10px] uppercase text-slate-400">Created</div><div className="mt-1 text-sm font-semibold text-white">{shortDate(selectedRun.createdAt)}</div></div><div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[10px] uppercase text-slate-400">Completed</div><div className="mt-1 text-sm font-semibold text-white">{shortDate(selectedRun.completedAt)}</div></div></div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-xs uppercase text-slate-400">Worker summary</div><p className="mt-2 text-sm text-slate-300">{summaryText(selectedRun.resultSummary)}</p>{selectedRun.errorMessage && <p className="mt-2 text-sm text-red-300">{selectedRun.errorMessage}</p>}</div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-xs uppercase text-slate-400">Runtime context</div><pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap text-xs text-slate-300">{JSON.stringify({ customerId: selectedRun.customerId, customerName: selectedRun.customerName, tenantId: selectedRun.tenantId, subscriptionId: selectedRun.subscriptionId, resourceGroup: selectedRun.resourceGroup, workspaceId: selectedRun.workspaceId, workspaceName: selectedRun.workspaceName, sourceModule: selectedRun.sourceModule, sourceRecordId: selectedRun.sourceRecordId, incidentId: selectedRun.incidentId, incidentTitle: selectedRun.incidentTitle, entityType: selectedRun.entityType, entityValue: selectedRun.entityValue, cveId: selectedRun.cveId, iocValue: selectedRun.iocValue, campaignId: selectedRun.campaignId, parameters: selectedRun.parameters, runtimeContext: selectedRun.runtimeContext }, null, 2)}</pre></div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="flex items-center justify-between"><div className="text-xs uppercase text-slate-400">Artifacts</div><div className="text-[10px] text-slate-500">HTML reports render in a sandboxed preview.</div></div><div className="mt-3 space-y-3">{(selectedRun.artifacts ?? []).length === 0 && <div className="text-sm text-slate-500">No artifacts loaded yet. Refresh the run after worker completion.</div>}{(selectedRun.artifacts ?? []).map((artifact) => <ArtifactPreview key={artifact.id} artifact={artifact} />)}</div></div>
              <div className="flex flex-wrap gap-2"><button onClick={() => void refreshRun(selectedRun)} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100">Refresh worker status</button>{canCancelRuns && ['queued','running','submitted'].includes(selectedRun.status) && <button onClick={() => void cancelRun(selectedRun)} className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100">Cancel</button>}{canDeleteHistory && <button onClick={() => void deleteRun(selectedRun)} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-100">Delete history</button>}</div>
            </div>}
          </div>
        </div>
      )}

      {mode === 'sources' && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            <h3 className="text-lg font-bold text-white">Notebook Sources</h3>
            <p className="text-sm text-slate-400">Default sources are seeded automatically and read from local VM GitHub mirrors under the configured cache root. Use the sync script before running catalog sync.</p>
            <div className="mt-4 divide-y divide-slate-800 rounded-xl border border-slate-800">
              {sources.map((source) => (
                <div key={source.id} className="p-3">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="font-semibold text-white">{source.name}</div>
                      <div className="text-xs text-slate-400">{source.repoUrl} · branch {source.branch}{source.pathFilter ? ` · path ${source.pathFilter}` : ''}</div>
                      <div className="mt-1 text-xs text-cyan-300">Local mirror: {source.localRepoPath || `${syncContext.localRepoRoot}/${source.repoName || 'repo'}`}</div>
                      <div className="mt-2 flex flex-wrap gap-2"><span className={`rounded-full border px-2 py-1 text-xs ${statusClass(source.enabled ? 'enabled' : 'disabled')}`}>{source.enabled ? 'Enabled' : 'Disabled'}</span><span className={`rounded-full border px-2 py-1 text-xs ${statusClass(source.lastSyncStatus ?? 'not synced')}`}>{source.lastSyncStatus ?? 'not synced'}</span>{source.lastSyncedAt && pill(`Last sync ${shortDate(source.lastSyncedAt)}`)}</div>
                      {source.lastSyncError && <div className="mt-2 text-xs text-red-300">{source.lastSyncError}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canSync && <button disabled={syncing !== null} onClick={() => void sync(source.id)} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100 disabled:opacity-50">Sync</button>}
                      {canManageSources && <button onClick={() => void toggleSource(source)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100">{source.enabled ? 'Disable' : 'Enable'}</button>}
                      {canManageSources && <button onClick={() => void deleteSource(source)} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-100">Delete</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            <h3 className="text-lg font-bold text-white">Add GitHub source</h3>
            <p className="text-sm text-slate-400">Phase 2 accepts GitHub repositories, but sync reads from the matching local VM mirror by default.</p>
            <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-3 text-xs text-cyan-100">Local root: {syncContext.localRepoRoot}. For custom repos, clone them under this root using the repository name, or set a source-specific NOTEBOOKOPS_LOCAL_REPO_*_PATH variable.</div>
            <div className="mt-4 space-y-2">
              <input value={newSource.name} onChange={(e) => setNewSource((p) => ({ ...p, name: e.target.value }))} placeholder="Display name" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
              <input value={newSource.repoUrl} onChange={(e) => setNewSource((p) => ({ ...p, repoUrl: e.target.value }))} placeholder="https://github.com/org/repo" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
              <input value={newSource.branch} onChange={(e) => setNewSource((p) => ({ ...p, branch: e.target.value }))} placeholder="master or main" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
              <input value={newSource.pathFilter} onChange={(e) => setNewSource((p) => ({ ...p, pathFilter: e.target.value }))} placeholder="Optional path filter e.g. Notebooks" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
              <button disabled={!canManageSources} onClick={() => void addSource()} className="w-full rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-100 disabled:opacity-50">Add source</button>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold text-white">Recent sync runs</h4>
              <div className="mt-2 max-h-72 space-y-2 overflow-auto text-xs">
                {syncRuns.length === 0 && <div className="text-slate-500">No sync runs yet.</div>}
                {syncRuns.map((run) => {
                  const details = (run.details && typeof run.details === 'object') ? run.details as Record<string, unknown> : {};
                  const syncMode = details.syncMode != null ? String(details.syncMode) : '';
                  const localRepoPath = details.localRepoPath != null ? String(details.localRepoPath) : '';
                  return <div key={run.id} className="rounded-lg border border-slate-800 bg-slate-900/60 p-2"><div className="flex items-center justify-between"><span className="font-semibold text-slate-200">{String(run.sourceName ?? 'Notebook source')}</span><span className={`rounded-full border px-2 py-0.5 ${statusClass(String(run.status ?? 'unknown'))}`}>{String(run.status ?? 'unknown')}</span></div><div className="mt-1 text-slate-400">{String(run.importedCount ?? 0)} imported · {String(run.updatedCount ?? 0)} updated · {String(run.skippedCount ?? 0)} skipped</div>{syncMode ? <div className="text-cyan-300">Mode: {syncMode}{localRepoPath ? ` · ${localRepoPath}` : ''}</div> : null}<div className="text-slate-500">{shortDate(run.createdAt)}</div>{run.errorMessage ? <div className="mt-1 text-red-300">{String(run.errorMessage)}</div> : null}</div>;
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === 'governance' && (
        <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
          <h3 className="text-lg font-bold text-white">NotebookOps Governance</h3>
          <p className="mt-1 max-w-4xl text-sm text-slate-300">Phase 4 introduces safe read-only KQL execution and Customer Master runtime context injection through the separate Python Notebook Worker. Guardrails remain enforced before submission, and only trusted notebooks should be run.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3"><div className="text-sm font-bold text-cyan-100">Safe preview</div><p className="mt-1 text-xs text-cyan-200/80">Shows title, source, markdown summary, last sync, tables, packages, URLs/APIs and KQL snippets.</p></div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"><div className="text-sm font-bold text-amber-100">Risk scanner</div><p className="mt-1 text-xs text-amber-200/80">Classifies notebooks into safe preview, read-only KQL, external enrichment, secrets/API keys, shell/file operations or blocked/unsafe.</p></div>
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3"><div className="text-sm font-bold text-red-100">Non-overridable</div><p className="mt-1 text-xs text-red-200/80">Customer workspace tokens/keys embedded in notebook files are always blocked. Runtime context must be injected by KloudSecure later.</p></div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3"><div className="text-sm font-bold text-white">Separate worker</div><p className="mt-1 text-xs text-slate-400">Execution is handled by artifacts/notebook-worker. Read-only KQL uses Azure Monitor Query with managed identity/default credential and never embeds workspace keys.</p></div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <div className="font-semibold text-white">Permission-gated guardrails</div>
            <p className="mt-1 text-xs text-slate-400">These permissions are visible in Masters → Permissions. They do not execute notebooks in Phase 2; they only document which future runner guardrails this role may override.</p>
            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {(guardrailPolicy?.guardrails ?? []).map((g) => <div key={g.id} className={`rounded-xl border p-3 ${g.nonOverridable ? 'border-red-500/30 bg-red-500/10' : g.allowedByPermission ? 'border-amber-500/30 bg-amber-500/10' : 'border-slate-800 bg-slate-950/50'}`}><div className="text-sm font-bold text-white">{g.label}</div><div className="mt-1 text-xs text-slate-300">{g.description}</div><div className="mt-2 text-xs text-slate-400">Default: {g.defaultAction} · {g.nonOverridable ? 'non-overridable' : g.allowedByPermission ? `override granted (${g.capability})` : `requires ${g.capability}`}</div></div>)}
              {!guardrailPolicy && <div className="text-sm text-slate-400">Guardrail policy could not be loaded.</div>}
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-sm text-slate-300">
            <div className="font-semibold text-white">Phase 2 guardrail rules</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Block shell commands, package installs and process execution by default.</li>
              <li>Block file writes/deletes and write-like APIs by default.</li>
              <li>Only read-only KQL should be considered for future execution unless an override capability exists.</li>
              <li>Secret/API key access and secret printing are blocked by default; raw detected secret values are never shown.</li>
              <li>Outbound calls require future allowlisting plus the external API override capability.</li>
              <li>Customer workspace tokens must never be stored in notebook files; future execution must use KloudSecure-controlled parameter injection.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
